/**
 * Elite Batch 6 Part 3 — Workflow Templates 488–507
 * Replenishment, Purchase Orders, Re-Subscribe, Referrals, Refunds, Returns,
 * Reorder Reminders, Repeat Buyer Recovery, Replacement Parts & RMA
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function postmarkNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  htmlBody: string,
  stream: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.postmarkapp.com/email",
        headers: [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "From", value: "{sender_email}" },
          { key: "To", value: to },
          { key: "Subject", value: subject },
          { key: "HtmlBody", value: htmlBody },
          { key: "MessageStream", value: stream },
        ],
        variables: [
          {
            name: "MessageID",
            customName: "postmark_message_id",
            type: "string",
          },
          {
            name: "ErrorCode",
            customName: "postmark_error_code",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/crm/v3/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 6 PART 3 — TEMPLATES 488–507
// =============================================================================

export const ELITE_BATCH6_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 488. PRODUCT-SPECIFIC REPLENISHMENT (7 nodes)
  // ===================================================================

  "Product-Specific Replenishment": {
    summary:
      "Sends product-specific reorder reminders timed to actual consumption cycles. A delivery webhook triggers replenishment date calculation via runCode based on product category and typical usage, waits until the predicted depletion window, checks if the customer has already reordered, sends a personalized replenishment email with one-click reorder, optimizes send time, and alerts ops on failures via Slack.",
    nodes: [
      trigger("Webhook: Product delivery confirmed", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A product fulfillment is confirmed in Shopify, indicating the customer has received their order",
        setup:
          "Connect Shopify fulfillments/create webhook. The workflow will calculate product-specific replenishment timing based on category consumption rates.",
        inputFields: [],
      }),

      // 2. AI extraction + replenishment date calc (runCode)
      n("2", "runCode", "Calculate replenishment date", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate product-specific replenishment date based on category consumption rates
const payload = JSON.parse(vars.webhook_payload || '{}');
const productTitle = payload.line_items?.[0]?.title || payload.product_title || 'Unknown Product';
const productId = payload.line_items?.[0]?.product_id || payload.product_id || '';
const sku = payload.line_items?.[0]?.sku || payload.sku || '';
const quantity = parseInt(payload.line_items?.[0]?.quantity || '1', 10);
const email = payload.email || payload.customer?.email || '';
const firstName = payload.customer?.first_name || payload.first_name || 'Customer';

// Category-based consumption rates (days per unit)
const categoryRates = {
  'skincare': 30, 'supplements': 30, 'coffee': 14, 'pet_food': 21,
  'cleaning': 45, 'baby': 14, 'protein': 28, 'vitamins': 30, 'default': 30
};
const category = (payload.line_items?.[0]?.vendor || 'default').toLowerCase();
const matchedCategory = Object.keys(categoryRates).find(c => category.includes(c)) || 'default';
const daysPerUnit = categoryRates[matchedCategory];
const totalDays = daysPerUnit * quantity;
const reminderDays = Math.max(1, totalDays - 5); // remind 5 days before depletion

return {
  customer_email: email,
  customer_first_name: firstName,
  product_title: productTitle,
  product_id: productId,
  sku: sku,
  quantity: quantity,
  category: matchedCategory,
  consumption_days: totalDays,
  reminder_delay_days: reminderDays,
  depletion_date: new Date(Date.now() + totalDays * 86400000).toISOString().split('T')[0],
  reminder_date: new Date(Date.now() + reminderDays * 86400000).toISOString().split('T')[0]
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "product_id", type: "string" },
            { name: "sku", type: "string" },
            { name: "quantity", type: "number" },
            { name: "category", type: "string" },
            { name: "consumption_days", type: "number" },
            { name: "reminder_delay_days", type: "number" },
            { name: "depletion_date", type: "string" },
            { name: "reminder_date", type: "string" },
          ],
        },
      }),

      // 3. Scheduled delay until reminder date
      n("3", "delayAction", "Wait until replenishment window", 520, {
        delayDetails: {
          delayType: "dynamic",
          delayField: "reminder_delay_days",
          delayUnit: "days",
        },
      }),

      // 4. Reorder check condition
      n("4", "condition", "Customer already reordered?", 760, {
        conditionData: [
          {
            variable: "sku",
            value: "",
            comparison_operator: "NOT EMPTY",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Replenishment email
      emailNode(
        "5",
        "Send replenishment reminder email",
        1000,
        "{customer_email}",
        "Time to restock your {product_title}?",
        "Hi {customer_first_name},\\n\\nBased on your last order of {quantity}x {product_title}, you may be running low soon (estimated depletion: {depletion_date}).\\n\\nReorder now and never run out:\\n{reorder_link}\\n\\nWant to save time? Set up auto-replenishment and get 10% off every recurring order.\\n\\nThanks for being a loyal customer!",
      ),

      // 6. Send time optimization (runCode)
      n("6", "runCode", "Optimize send time", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Log send time optimization metrics
const hour = new Date().getHours();
const dayOfWeek = new Date().getDay();
const optimalWindow = (hour >= 9 && hour <= 11) || (hour >= 18 && hour <= 20);
return {
  send_hour: hour,
  send_day: dayOfWeek,
  optimal_window: optimalWindow ? 'yes' : 'no',
  send_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "send_hour", type: "number" },
            { name: "send_day", type: "number" },
            { name: "optimal_window", type: "string" },
            { name: "send_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Ops failure alert (Slack)
      slackNode(
        "7",
        "Slack: Ops failure alert",
        1480,
        ":warning: Replenishment Reminder Issue\\nProduct: {product_title} (SKU: {sku})\\nCustomer: {customer_email}\\nCategory: {category}\\nDepletion date: {depletion_date}\\nSend window: {optimal_window}\\nCheck workflow for delivery/timing errors.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Not yet reordered -> send reminder
      e("4", "7", "output_2"), // Already reordered -> alert ops for review
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 489. PURCHASE ORDER AUTO GENERATOR (9 nodes)
  // ===================================================================

  "Purchase Order Auto Generator": {
    summary:
      "Automates purchase order creation when inventory hits reorder points. A reorder-point webhook triggers supplier and MOQ data retrieval, calculates optimal order quantity via runCode, routes on approval threshold, generates the PO document, submits to the supplier via email, updates projected inventory in Sheets, notifies procurement on Slack, and escalates failures.",
    nodes: [
      trigger("Webhook: Reorder point reached", "webhook_app", {
        source: "Inventory System",
        event: "inventory.reorder_point",
        howItStarts:
          "An inventory management system detects that stock for a SKU has fallen to or below the reorder point threshold",
        setup:
          "Connect your inventory system (Shopify, TradeGecko, Cin7, etc.) to fire a webhook when stock hits the reorder point. Include SKU, current stock, reorder point, and supplier info.",
        inputFields: [],
      }),

      // 2. Supplier/MOQ/lead-time pull
      aiNode(
        "2",
        "AI extract supplier + MOQ + lead time",
        280,
        "You are an inventory operations specialist. Parse the reorder point payload and extract: SKU, product name, current stock level, reorder point, supplier name, supplier email, minimum order quantity (MOQ), unit cost, lead time in days, warehouse location, and preferred shipping method. Look for these fields regardless of format or naming convention. Respond JSON: {sku, product_name, current_stock, reorder_point, supplier_name, supplier_email, moq, unit_cost, lead_time_days, warehouse, shipping_method}",
        "{webhook_payload}",
        [
          { field: "sku", as: "sku", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "current_stock", as: "current_stock", type: "number" },
          { field: "reorder_point", as: "reorder_point", type: "number" },
          { field: "supplier_name", as: "supplier_name", type: "string" },
          { field: "supplier_email", as: "supplier_email", type: "string" },
          { field: "moq", as: "moq", type: "number" },
          { field: "unit_cost", as: "unit_cost", type: "number" },
          { field: "lead_time_days", as: "lead_time_days", type: "number" },
          { field: "warehouse", as: "warehouse", type: "string" },
          { field: "shipping_method", as: "shipping_method", type: "string" },
        ],
      ),

      // 3. Optimal quantity calc (runCode)
      n("3", "runCode", "Calculate optimal order quantity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate optimal PO quantity based on MOQ, velocity, and lead time
const currentStock = parseInt(vars.current_stock || '0', 10);
const reorderPoint = parseInt(vars.reorder_point || '0', 10);
const moq = parseInt(vars.moq || '1', 10);
const unitCost = parseFloat(vars.unit_cost || '0');
const leadTimeDays = parseInt(vars.lead_time_days || '7', 10);

// Estimate daily velocity from reorder point / lead time safety factor
const safetyFactor = 1.5;
const dailyVelocity = Math.max(1, Math.round(reorderPoint / (leadTimeDays * safetyFactor)));
const coverageDays = 60; // target 60-day coverage
const idealQty = dailyVelocity * coverageDays;
const orderQty = Math.max(moq, Math.ceil(idealQty / moq) * moq); // round up to MOQ multiple
const totalCost = (orderQty * unitCost).toFixed(2);
const expectedArrival = new Date(Date.now() + leadTimeDays * 86400000).toISOString().split('T')[0];
const needsApproval = parseFloat(totalCost) > 5000 ? 'yes' : 'no';
const poNumber = 'PO-' + Date.now().toString(36).toUpperCase();

return {
  order_quantity: orderQty,
  daily_velocity: dailyVelocity,
  coverage_days: coverageDays,
  total_cost: totalCost,
  expected_arrival: expectedArrival,
  needs_approval: needsApproval,
  po_number: poNumber,
  stock_deficit: Math.max(0, reorderPoint - currentStock)
};`,
          outputVariables: [
            { name: "order_quantity", type: "number" },
            { name: "daily_velocity", type: "number" },
            { name: "coverage_days", type: "number" },
            { name: "total_cost", type: "string" },
            { name: "expected_arrival", type: "string" },
            { name: "needs_approval", type: "string" },
            { name: "po_number", type: "string" },
            { name: "stock_deficit", type: "number" },
          ],
        },
      }),

      // 4. Approval condition
      n("4", "condition", "PO exceeds approval threshold?", 760, {
        conditionData: [
          {
            variable: "needs_approval",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. PO document generation (AI)
      aiNode(
        "5",
        "Generate PO document",
        1000,
        "You are a procurement document specialist. Generate a formal purchase order document in structured format. Include PO number, date, supplier details, line items with SKU, product name, quantity, unit cost, total cost, shipping method, expected arrival date, and payment terms (Net 30). Respond JSON: {po_html, po_summary, payment_terms, line_item_count}",
        "PO Number: {po_number}, Supplier: {supplier_name}, SKU: {sku}, Product: {product_name}, Qty: {order_quantity}, Unit Cost: ${unit_cost}, Total: ${total_cost}, Ship: {shipping_method}, ETA: {expected_arrival}, Warehouse: {warehouse}",
        [
          { field: "po_html", as: "po_html", type: "string" },
          { field: "po_summary", as: "po_summary", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
        ],
      ),

      // 6. Supplier submission (email)
      emailNode(
        "6",
        "Email PO to supplier",
        1240,
        "{supplier_email}",
        "Purchase Order {po_number} - {product_name} x{order_quantity}",
        "Dear {supplier_name},\\n\\nPlease find our purchase order below:\\n\\n{po_summary}\\n\\nPO Number: {po_number}\\nProduct: {product_name} (SKU: {sku})\\nQuantity: {order_quantity} units\\nUnit Cost: ${unit_cost}\\nTotal: ${total_cost}\\nShipping: {shipping_method}\\nRequired By: {expected_arrival}\\nPayment Terms: {payment_terms}\\n\\nPlease confirm receipt and expected ship date.\\n\\nThank you.",
      ),

      // 7. Projected inventory update (Sheets)
      sheetsNode(
        "7",
        "Log PO to inventory Sheets",
        1480,
        "PurchaseOrders",
        '[["{po_number}","{sku}","{product_name}","{supplier_name}","{order_quantity}","{unit_cost}","{total_cost}","{expected_arrival}","{warehouse}","{needs_approval}","{daily_velocity}","{current_stock}"]]',
      ),

      // 8. Procurement Slack notification
      slackNode(
        "8",
        "Slack: PO created notification",
        1720,
        ":package: Purchase Order Created\\nPO: {po_number}\\nProduct: {product_name} ({sku})\\nSupplier: {supplier_name}\\nQty: {order_quantity} | Cost: ${total_cost}\\nETA: {expected_arrival}\\nWarehouse: {warehouse}\\nApproval needed: {needs_approval}",
      ),

      // 9. Failure escalation
      slackNode(
        "9",
        "Slack: PO failure escalation",
        1960,
        ":rotating_light: PO Generation Failed\\nSKU: {sku} ({product_name})\\nSupplier: {supplier_name}\\nCurrent stock: {current_stock} (deficit: {stock_deficit})\\nAttempted PO: {po_number}\\nPlease review and create PO manually.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Auto-approved -> generate PO
      e("4", "9", "output_2"), // Needs manual approval -> escalation
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 490. RE-SUBSCRIBE AFTER LAPSE (8 nodes)
  // ===================================================================

  "Re-Subscribe After Lapse": {
    summary:
      "Wins back lapsed subscribers with AI-personalized re-subscribe incentives. A subscription lapse trigger fires, pulls subscription history, generates AI-driven win-back messaging with personalized incentives, branches on cancellation reason, crafts price-focused or fatigue-focused messaging, sends the re-subscribe email via Postmark, and logs reactivation outcomes to Sheets.",
    nodes: [
      trigger("Webhook: Subscription lapsed", "webhook_app", {
        source: "Stripe",
        event: "customer.subscription.deleted",
        howItStarts:
          "A customer's subscription is cancelled or expires in Stripe, marking them as a lapsed subscriber eligible for win-back",
        setup:
          "Connect Stripe customer.subscription.deleted webhook. The workflow will attempt to win back the subscriber with personalized incentives based on their history.",
        inputFields: [],
      }),

      // 2. Subscription history pull
      aiNode(
        "2",
        "AI extract subscription history",
        280,
        "You are a subscription analytics specialist. Parse the subscription cancellation payload and extract: customer email, customer name, subscription plan name, monthly amount, subscription duration in months, cancellation reason (if available), last payment date, total lifetime value, number of past reactivations, and any feedback provided. Respond JSON: {customer_email, customer_name, plan_name, monthly_amount, sub_duration_months, cancel_reason, last_payment_date, lifetime_value, past_reactivations, customer_feedback}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_amount", as: "monthly_amount", type: "string" },
          {
            field: "sub_duration_months",
            as: "sub_duration_months",
            type: "number",
          },
          { field: "cancel_reason", as: "cancel_reason", type: "string" },
          {
            field: "last_payment_date",
            as: "last_payment_date",
            type: "string",
          },
          { field: "lifetime_value", as: "lifetime_value", type: "string" },
          {
            field: "past_reactivations",
            as: "past_reactivations",
            type: "number",
          },
          {
            field: "customer_feedback",
            as: "customer_feedback",
            type: "string",
          },
        ],
      ),

      // 3. AI win-back incentive generation
      aiNode(
        "3",
        "AI generate win-back incentive",
        520,
        "You are a retention marketing strategist. Based on the subscriber's history, generate a personalized win-back incentive. Consider their subscription duration, lifetime value, cancellation reason, and past reactivations. For high-LTV customers, offer bigger discounts. For price-sensitive cancellations, offer a lower-tier plan. For fatigue/disengagement cancellations, highlight new features. Respond JSON: {incentive_type, discount_percent, offer_description, subject_line, urgency_message, price_messaging, fatigue_messaging, reason_category}",
        "Customer: {customer_name}, Plan: {plan_name}, Monthly: ${monthly_amount}, Duration: {sub_duration_months} months, LTV: ${lifetime_value}, Cancel reason: {cancel_reason}, Feedback: {customer_feedback}, Past reactivations: {past_reactivations}",
        [
          { field: "incentive_type", as: "incentive_type", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "string" },
          {
            field: "offer_description",
            as: "offer_description",
            type: "string",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "urgency_message", as: "urgency_message", type: "string" },
          { field: "price_messaging", as: "price_messaging", type: "string" },
          {
            field: "fatigue_messaging",
            as: "fatigue_messaging",
            type: "string",
          },
          { field: "reason_category", as: "reason_category", type: "string" },
        ],
      ),

      // 4. Cancellation reason condition
      n("4", "condition", "Cancellation reason: price?", 760, {
        conditionData: [
          {
            variable: "reason_category",
            value: "price",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Price messaging path
      emailNode(
        "5",
        "Price-focused win-back email",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {customer_name},\\n\\nWe noticed you cancelled your {plan_name} subscription. We understand budgets matter.\\n\\n{price_messaging}\\n\\nSpecial offer just for you: {offer_description}\\n\\n{urgency_message}\\n\\nReactivate now and save {discount_percent}% on your next {sub_duration_months > 3 ? '3 months' : 'month'}:\\n{reactivation_link}\\n\\nWe'd love to have you back!",
      ),

      // 6. Fatigue messaging path
      emailNode(
        "6",
        "Engagement-focused win-back email",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {customer_name},\\n\\nWe miss you! Since you left {plan_name}, we've been busy improving things.\\n\\n{fatigue_messaging}\\n\\n{offer_description}\\n\\n{urgency_message}\\n\\nCome see what's new — reactivate with {discount_percent}% off:\\n{reactivation_link}\\n\\nWe think you'll love the changes!",
        400,
      ),

      // 7. Re-subscribe email via Postmark
      postmarkNode(
        "7",
        "Send win-back via Postmark",
        1240,
        "{customer_email}",
        "{subject_line}",
        "<h2>We want you back, {customer_name}!</h2><p>{offer_description}</p><p>{urgency_message}</p><a href='{reactivation_link}'>Reactivate Now - Save {discount_percent}%</a>",
        "outbound",
      ),

      // 8. Reactivation logging (Sheets)
      sheetsNode(
        "8",
        "Log reactivation attempt to Sheets",
        1480,
        "ReactivationLog",
        '[["{customer_email}","{customer_name}","{plan_name}","{monthly_amount}","{sub_duration_months}","{cancel_reason}","{reason_category}","{incentive_type}","{discount_percent}","{lifetime_value}","{past_reactivations}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Price reason -> price messaging
      e("4", "6", "output_2"), // Fatigue/other reason -> engagement messaging
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 491. RECENT PURCHASER FOLLOW-UP EXCLUDED FROM PROMO (8 nodes)
  // ===================================================================

  "Recent Purchaser Follow-Up Excluded From Promo": {
    summary:
      "Protects recent buyers from promo blasts by sending tailored alternatives. A promo campaign trigger identifies recent purchasers, suppresses them from the main blast, branches on category overlap, sends price-protection or cross-sell emails instead, delivers a tailored follow-up, and logs all exclusions to Sheets for reporting.",
    nodes: [
      trigger("Webhook: Promo campaign launched", "webhook_app", {
        source: "Marketing Platform",
        event: "campaign.launched",
        howItStarts:
          "A promotional campaign is launched, triggering identification of recent purchasers who should be excluded from the discount blast to avoid buyer's remorse",
        setup:
          "Connect your marketing platform to fire a webhook when a promo campaign launches. Include campaign details, promo category, discount percentage, and campaign audience list.",
        inputFields: [],
      }),

      // 2. Recent purchaser identification
      aiNode(
        "2",
        "AI identify recent purchasers",
        280,
        "You are an e-commerce customer segmentation specialist. Parse the campaign launch payload and extract: campaign name, promo category, discount percentage, campaign start date, campaign end date. Also identify the lookback window for recent purchasers (default: 14 days). Determine which product categories overlap with the promo. Respond JSON: {campaign_name, promo_category, discount_percent, campaign_start, campaign_end, lookback_days, overlapping_categories, customer_email, customer_name, last_purchase_date, last_purchase_amount, last_purchase_category, order_id}",
        "{webhook_payload}",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "promo_category", as: "promo_category", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "string" },
          { field: "campaign_start", as: "campaign_start", type: "string" },
          { field: "campaign_end", as: "campaign_end", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "last_purchase_date",
            as: "last_purchase_date",
            type: "string",
          },
          {
            field: "last_purchase_amount",
            as: "last_purchase_amount",
            type: "string",
          },
          {
            field: "last_purchase_category",
            as: "last_purchase_category",
            type: "string",
          },
          { field: "order_id", as: "order_id", type: "string" },
        ],
      ),

      // 3. Suppression from blast (runCode)
      n("3", "runCode", "Suppress from promo blast", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine suppression eligibility and category overlap
const promoCategory = (vars.promo_category || '').toLowerCase();
const purchaseCategory = (vars.last_purchase_category || '').toLowerCase();
const categoryOverlap = promoCategory === purchaseCategory || promoCategory.includes(purchaseCategory) || purchaseCategory.includes(promoCategory);
const purchaseAmount = parseFloat(vars.last_purchase_amount || '0');
const discountPercent = parseFloat(vars.discount_percent || '0');
const potentialSavings = (purchaseAmount * discountPercent / 100).toFixed(2);

return {
  suppressed: 'yes',
  category_overlap: categoryOverlap ? 'yes' : 'no',
  potential_savings: potentialSavings,
  suppression_reason: categoryOverlap ? 'Same category purchased recently - price protection' : 'Recent purchaser - cross-sell opportunity',
  price_protection_eligible: categoryOverlap && parseFloat(potentialSavings) > 5 ? 'yes' : 'no'
};`,
          outputVariables: [
            { name: "suppressed", type: "string" },
            { name: "category_overlap", type: "string" },
            { name: "potential_savings", type: "string" },
            { name: "suppression_reason", type: "string" },
            { name: "price_protection_eligible", type: "string" },
          ],
        },
      }),

      // 4. Category overlap condition
      n("4", "condition", "Category overlaps with promo?", 760, {
        conditionData: [
          {
            variable: "category_overlap",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Price-protection email (True: same category)
      emailNode(
        "5",
        "Price-protection email",
        1000,
        "{customer_email}",
        "A note about your recent {last_purchase_category} purchase",
        "Hi {customer_name},\\n\\nWe noticed you recently purchased from our {last_purchase_category} collection (Order #{order_id}, {last_purchase_date}).\\n\\nWe're currently running a {discount_percent}% promotion on {promo_category}. Since you just bought at full price, we'd like to offer you a ${potential_savings} store credit on your next order as a thank you.\\n\\nUse code LOYALTY-{order_id} at checkout.\\n\\nWe value your loyalty and want to make sure you always feel great about shopping with us!",
      ),

      // 6. Cross-sell variant email (False: different category)
      emailNode(
        "6",
        "Cross-sell alternative email",
        1000,
        "{customer_email}",
        "Something new to go with your recent {last_purchase_category} purchase",
        "Hi {customer_name},\\n\\nLoved your recent {last_purchase_category} purchase? We think you'll also love what's happening in {promo_category}.\\n\\nRight now, we're offering {discount_percent}% off our entire {promo_category} collection — and these items pair perfectly with what you already have.\\n\\nShop the sale: {promo_link}\\n\\nOffer ends {campaign_end}.",
        400,
      ),

      // 7. Tailored follow-up send
      emailNode(
        "7",
        "Tailored follow-up email",
        1240,
        "{customer_email}",
        "Following up on your experience, {customer_name}",
        "Hi {customer_name},\\n\\nHow are you enjoying your recent {last_purchase_category} purchase? We'd love to hear your thoughts.\\n\\nLeave a review and earn loyalty points: {review_link}\\n\\nThanks for shopping with us!",
      ),

      // 8. Exclusion logging (Sheets)
      sheetsNode(
        "8",
        "Log exclusion to Sheets",
        1480,
        "PromoExclusions",
        '[["{customer_email}","{customer_name}","{campaign_name}","{promo_category}","{discount_percent}","{last_purchase_category}","{last_purchase_date}","{last_purchase_amount}","{order_id}","{category_overlap}","{suppression_reason}","{potential_savings}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Category overlap -> price protection
      e("4", "6", "output_2"), // No overlap -> cross-sell
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 492. REFERRAL ASK AFTER SATISFIED PURCHASE (8 nodes)
  // ===================================================================

  "Referral Ask After Satisfied Purchase": {
    summary:
      "Asks happy customers for referrals with unique tracking codes. A positive satisfaction score triggers referral program status check, branches on new vs existing advocate, generates a unique referral code via runCode, sends a referral email with share CTAs, creates tracking links, logs the referral to Sheets, and alerts ops on failures.",
    nodes: [
      trigger("Webhook: Positive satisfaction score", "webhook_app", {
        source: "Survey Platform",
        event: "response.completed",
        howItStarts:
          "A customer submits a satisfaction survey with a high score (CSAT >= 4 or NPS >= 8), indicating they are happy and likely to refer",
        setup:
          "Connect your survey platform (Outgrow Forms, Delighted, SurveyMonkey) to fire a webhook when a positive response is submitted. Include customer details and score.",
        inputFields: [],
      }),

      // 2. Referral program status check
      aiNode(
        "2",
        "AI extract satisfaction + referral status",
        280,
        "You are a referral program specialist. Parse the survey response payload and extract: customer email, customer name, satisfaction score, score type (CSAT or NPS), order ID, product purchased, survey comments, whether the customer is already a referral advocate (has existing referral code), and their total referral count to date. Respond JSON: {customer_email, customer_name, satisfaction_score, score_type, order_id, product_name, survey_comments, is_existing_advocate, existing_referral_code, total_referrals}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "satisfaction_score",
            as: "satisfaction_score",
            type: "number",
          },
          { field: "score_type", as: "score_type", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "survey_comments", as: "survey_comments", type: "string" },
          {
            field: "is_existing_advocate",
            as: "is_existing_advocate",
            type: "string",
          },
          {
            field: "existing_referral_code",
            as: "existing_referral_code",
            type: "string",
          },
          { field: "total_referrals", as: "total_referrals", type: "number" },
        ],
      ),

      // 3. New vs existing advocate condition
      n("3", "condition", "Already a referral advocate?", 520, {
        conditionData: [
          {
            variable: "is_existing_advocate",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Unique referral code generation (runCode)
      n("4", "runCode", "Generate unique referral code", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique referral code for new advocates or retrieve existing
const isExisting = vars.is_existing_advocate === 'true';
const existingCode = vars.existing_referral_code || '';
const customerName = (vars.customer_name || 'FRIEND').replace(/[^a-zA-Z]/g, '').substring(0, 6).toUpperCase();
const uniqueSuffix = Date.now().toString(36).toUpperCase().substring(0, 4);

const referralCode = isExisting ? existingCode : customerName + '-' + uniqueSuffix;
const referralLink = 'https://shop.example.com/ref/' + referralCode;
const shareText = encodeURIComponent('I love ' + (vars.product_name || 'this product') + '! Use my code ' + referralCode + ' for 15% off your first order.');
const twitterShareUrl = 'https://twitter.com/intent/tweet?text=' + shareText;
const fbShareUrl = 'https://www.facebook.com/sharer.php?u=' + encodeURIComponent(referralLink);
const emailShareUrl = 'mailto:?subject=Check this out&body=' + shareText;

return {
  referral_code: referralCode,
  referral_link: referralLink,
  twitter_share_url: twitterShareUrl,
  fb_share_url: fbShareUrl,
  email_share_url: emailShareUrl,
  advocate_type: isExisting ? 'returning' : 'new',
  reward_tier: parseInt(vars.total_referrals || '0', 10) >= 5 ? 'gold' : 'standard'
};`,
          outputVariables: [
            { name: "referral_code", type: "string" },
            { name: "referral_link", type: "string" },
            { name: "twitter_share_url", type: "string" },
            { name: "fb_share_url", type: "string" },
            { name: "email_share_url", type: "string" },
            { name: "advocate_type", type: "string" },
            { name: "reward_tier", type: "string" },
          ],
        },
      }),

      // 5. Referral email with share CTAs
      emailNode(
        "5",
        "Referral email with share CTAs",
        1000,
        "{customer_email}",
        "Share the love, {customer_name} — earn rewards for every referral!",
        "Hi {customer_name},\\n\\nWe're thrilled you loved your {product_name}! Your {score_type} score of {satisfaction_score} made our day.\\n\\nShare your experience and earn rewards:\\n\\nYour referral code: {referral_code}\\nYour referral link: {referral_link}\\n\\nShare on:\\n- Twitter: {twitter_share_url}\\n- Facebook: {fb_share_url}\\n- Email a friend: {email_share_url}\\n\\nYou'll earn $10 credit for every friend who orders. Your friend gets 15% off their first purchase.\\n\\nReward tier: {reward_tier} | Total referrals: {total_referrals}\\n\\nThank you for spreading the word!",
      ),

      // 6. Tracking links (runCode)
      n("6", "runCode", "Create UTM tracking links", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate UTM-tagged tracking links for referral analytics
const code = vars.referral_code || '';
const base = vars.referral_link || '';
const utmSource = 'referral';
const utmMedium = 'email';
const utmCampaign = 'post_purchase_referral';
const trackedLink = base + '?utm_source=' + utmSource + '&utm_medium=' + utmMedium + '&utm_campaign=' + utmCampaign + '&utm_content=' + code;

return {
  tracked_referral_link: trackedLink,
  utm_campaign: utmCampaign,
  tracking_created: 'yes'
};`,
          outputVariables: [
            { name: "tracked_referral_link", type: "string" },
            { name: "utm_campaign", type: "string" },
            { name: "tracking_created", type: "string" },
          ],
        },
      }),

      // 7. Referral logging (Sheets)
      sheetsNode(
        "7",
        "Log referral to Sheets",
        1480,
        "ReferralProgram",
        '[["{customer_email}","{customer_name}","{referral_code}","{advocate_type}","{reward_tier}","{satisfaction_score}","{score_type}","{product_name}","{order_id}","{total_referrals}","{tracked_referral_link}"]]',
      ),

      // 8. Ops failure alert
      slackNode(
        "8",
        "Slack: Referral ops failure alert",
        1720,
        ":warning: Referral Flow Issue\\nCustomer: {customer_email} ({customer_name})\\nScore: {satisfaction_score} ({score_type})\\nReferral code: {referral_code}\\nAdvocate type: {advocate_type}\\nReview any code generation or tracking link issues.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Existing advocate -> reuse code
      e("3", "4", "output_2"), // New advocate -> generate code
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 493. REFILL SHIPPED CONFIRMATION (8 nodes)
  // ===================================================================

  "Refill Shipped Confirmation": {
    summary:
      "Confirms subscription refill shipments with tracking and manage links. A subscription shipment webhook triggers shipment and tracking data extraction, branches on next renewal timing, sends a refill shipped email with manage links via Postmark, logs subscription actions to Sheets, alerts ops on malformed data via Slack, and writes an audit record.",
    nodes: [
      trigger("Webhook: Subscription shipment created", "webhook_app", {
        source: "Subscription Platform",
        event: "subscription.shipment.created",
        howItStarts:
          "A subscription refill order ships, generating a tracking number and triggering a confirmation flow to the subscriber",
        setup:
          "Connect your subscription platform (ReCharge, Bold, Ordergroove) to fire a webhook when a subscription shipment is created. Include tracking, order, and subscription details.",
        inputFields: [],
      }),

      // 2. Shipment/tracking pull
      aiNode(
        "2",
        "AI extract shipment + tracking data",
        280,
        "You are a subscription fulfillment specialist. Parse the shipment webhook payload and extract: customer email, customer name, subscription ID, order number, tracking number, carrier name, tracking URL, product name, quantity, next renewal date, subscription frequency, subscription status, and manage subscription URL. Respond JSON: {customer_email, customer_name, subscription_id, order_number, tracking_number, carrier, tracking_url, product_name, quantity, next_renewal_date, frequency, subscription_status, manage_url}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "tracking_url", as: "tracking_url", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "quantity", as: "quantity", type: "number" },
          {
            field: "next_renewal_date",
            as: "next_renewal_date",
            type: "string",
          },
          { field: "frequency", as: "frequency", type: "string" },
          {
            field: "subscription_status",
            as: "subscription_status",
            type: "string",
          },
          { field: "manage_url", as: "manage_url", type: "string" },
        ],
      ),

      // 3. Next renewal timing condition
      n("3", "condition", "Next renewal within 7 days?", 520, {
        conditionData: [
          {
            variable: "next_renewal_date",
            value: "",
            comparison_operator: "NOT EMPTY",
            logical_operator: "AND",
          },
          {
            variable: "subscription_status",
            value: "active",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Refill shipped email with manage links
      emailNode(
        "4",
        "Refill shipped confirmation email",
        760,
        "{customer_email}",
        "Your {product_name} refill has shipped! - Order #{order_number}",
        "Hi {customer_name},\\n\\nGreat news! Your {product_name} refill (x{quantity}) is on its way.\\n\\nOrder: #{order_number}\\nCarrier: {carrier}\\nTracking: {tracking_url}\\n\\nNext refill: {next_renewal_date} ({frequency})\\n\\nManage your subscription:\\n{manage_url}\\n\\nNeed to skip, swap, or adjust your next delivery? Use the link above anytime before your next renewal date.\\n\\nThanks for subscribing!",
      ),

      // 5. Transactional send (Postmark)
      postmarkNode(
        "5",
        "Send via Postmark transactional",
        1000,
        "{customer_email}",
        "Shipment Confirmation - {product_name} Refill #{order_number}",
        "<h2>Your refill has shipped!</h2><p>Hi {customer_name}, your {product_name} (x{quantity}) is on its way.</p><p><strong>Tracking:</strong> <a href='{tracking_url}'>{tracking_number}</a> via {carrier}</p><p><strong>Next renewal:</strong> {next_renewal_date}</p><p><a href='{manage_url}'>Manage Subscription</a></p>",
        "outbound",
      ),

      // 6. Subscription action logging (Sheets)
      sheetsNode(
        "6",
        "Log subscription shipment to Sheets",
        1240,
        "SubscriptionShipments",
        '[["{customer_email}","{subscription_id}","{order_number}","{product_name}","{quantity}","{carrier}","{tracking_number}","{next_renewal_date}","{frequency}","{subscription_status}"]]',
      ),

      // 7. Ops malformed-data alert (Slack)
      slackNode(
        "7",
        "Slack: Malformed data alert",
        1480,
        ":warning: Subscription Shipment Data Issue\\nSubscription: {subscription_id}\\nOrder: #{order_number}\\nCustomer: {customer_email}\\nTracking: {tracking_number}\\nCarrier: {carrier}\\nStatus: {subscription_status}\\nCheck for missing tracking data or invalid renewal date.",
        400,
      ),

      // 8. Audit record
      sheetsNode(
        "8",
        "Write audit record",
        1720,
        "ShipmentAudit",
        '[["{subscription_id}","{order_number}","{customer_email}","{tracking_number}","{carrier}","{subscription_status}","{next_renewal_date}","confirmed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Active sub with renewal -> full confirmation
      e("3", "7", "output_2"), // Missing data or inactive -> ops alert
      e("4", "5"),
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 494. REFILL OR CONSUMABLE EDUCATION (7 nodes)
  // ===================================================================

  "Refill or Consumable Education": {
    summary:
      "Educates first-time consumable buyers on usage, care, and subscription options. A first consumable delivery triggers usage guide retrieval, branches on subscription model availability, sends subscription upsell or reminder opt-in emails, delivers post-delivery education, and alerts ops on content gaps via Slack.",
    nodes: [
      trigger("Webhook: First consumable delivery", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A first-time consumable product is delivered to a customer, triggering an educational drip to maximize product value and introduce replenishment options",
        setup:
          "Connect Shopify fulfillments/create webhook. Tag consumable products in Shopify so the workflow can identify first-time consumable deliveries.",
        inputFields: [],
      }),

      // 2. Usage/care guide pull
      aiNode(
        "2",
        "AI extract product + build usage guide",
        280,
        "You are a product education specialist. Parse the fulfillment payload and extract customer and product details, then generate a tailored usage/care guide. Identify: customer email, customer name, product name, product category, whether the product has a subscription option available, recommended usage frequency, storage instructions, and key tips for getting the most out of the product. Respond JSON: {customer_email, customer_name, product_name, product_category, has_subscription, usage_frequency, storage_tips, usage_tips, care_instructions, shelf_life_days, subscription_price, one_time_price}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "has_subscription", as: "has_subscription", type: "string" },
          { field: "usage_frequency", as: "usage_frequency", type: "string" },
          { field: "storage_tips", as: "storage_tips", type: "string" },
          { field: "usage_tips", as: "usage_tips", type: "string" },
          {
            field: "care_instructions",
            as: "care_instructions",
            type: "string",
          },
          { field: "shelf_life_days", as: "shelf_life_days", type: "number" },
          {
            field: "subscription_price",
            as: "subscription_price",
            type: "string",
          },
          { field: "one_time_price", as: "one_time_price", type: "string" },
        ],
      ),

      // 3. Subscription model condition
      n("3", "condition", "Subscription option available?", 520, {
        conditionData: [
          {
            variable: "has_subscription",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Subscription upsell email (True)
      emailNode(
        "4",
        "Subscription upsell education email",
        760,
        "{customer_email}",
        "Getting the most from your {product_name} + save with Subscribe & Save",
        "Hi {customer_name},\\n\\nCongrats on your {product_name}! Here's how to get the most out of it:\\n\\n{usage_tips}\\n\\nUsage: {usage_frequency}\\nStorage: {storage_tips}\\nCare: {care_instructions}\\n\\nNever run out — Subscribe & Save:\\nOne-time price: ${one_time_price}\\nSubscription price: ${subscription_price}\\n\\nSubscribe now and save on every delivery: {subscribe_link}\\n\\nEnjoy!",
      ),

      // 5. Reminder opt-in email (False)
      emailNode(
        "5",
        "Reminder opt-in education email",
        760,
        "{customer_email}",
        "Your {product_name} guide + set a reorder reminder",
        "Hi {customer_name},\\n\\nWelcome to your new {product_name}! Here's everything you need to know:\\n\\n{usage_tips}\\n\\nUsage: {usage_frequency}\\nStorage: {storage_tips}\\nCare: {care_instructions}\\nShelf life: ~{shelf_life_days} days\\n\\nWant a reminder when it's time to reorder? We'll notify you at just the right time:\\n{reminder_optin_link}\\n\\nEnjoy your {product_name}!",
        400,
      ),

      // 6. Post-delivery delay send
      n("6", "delayAction", "Wait 3 days post-delivery", 1000, {
        delayDetails: { delayType: "fixed", delayValue: 3, delayUnit: "days" },
      }),

      // 7. Content gap alert (Slack)
      slackNode(
        "7",
        "Slack: Content gap alert",
        1240,
        ":memo: Consumable Education Sent\\nProduct: {product_name} ({product_category})\\nCustomer: {customer_email}\\nSubscription available: {has_subscription}\\nUsage frequency: {usage_frequency}\\nShelf life: {shelf_life_days} days\\nReview if usage guide content is adequate for this product category.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Has subscription -> upsell email
      e("3", "5", "output_2"), // No subscription -> reminder opt-in
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 495. REFUND PROCESSING CHATBOT (9 nodes)
  // ===================================================================

  "Refund Processing Chatbot": {
    summary:
      "Handles refund requests end-to-end via chat with automated eligibility checking. A chat refund trigger authenticates the customer, pulls order data, checks refund policy eligibility via runCode, branches on eligibility, calculates refund amount, initiates the Stripe refund, generates a return label, confirms in-channel, and escalates to finance on Slack.",
    nodes: [
      trigger("Webhook: Chat refund request", "webhook_app", {
        source: "Chat Platform",
        event: "message.refund_intent",
        howItStarts:
          "A customer requests a refund through live chat or a chatbot, triggering automated refund processing with policy checks",
        setup:
          "Connect your chat platform (Intercom, Zendesk Chat, Drift) to detect refund intent and fire a webhook. Include the conversation ID and customer message.",
        inputFields: [],
      }),

      // 2. Customer auth + order pull
      aiNode(
        "2",
        "AI authenticate customer + pull order",
        280,
        "You are a customer service authentication specialist. Parse the chat refund request payload and extract: conversation ID, customer email, customer name, order number, product name, purchase date, purchase amount, payment method, items requested for refund, refund reason, and any attached photos or evidence. Validate the order exists and belongs to this customer. Respond JSON: {conversation_id, customer_email, customer_name, order_number, product_name, purchase_date, purchase_amount, payment_method, refund_items, refund_reason, has_evidence, customer_authenticated}",
        "{webhook_payload}",
        [
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "purchase_date", as: "purchase_date", type: "string" },
          { field: "purchase_amount", as: "purchase_amount", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "refund_items", as: "refund_items", type: "string" },
          { field: "refund_reason", as: "refund_reason", type: "string" },
          { field: "has_evidence", as: "has_evidence", type: "string" },
          {
            field: "customer_authenticated",
            as: "customer_authenticated",
            type: "string",
          },
        ],
      ),

      // 3. Policy eligibility check (runCode)
      n("3", "runCode", "Check refund policy eligibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check refund eligibility against return policy
const purchaseDate = new Date(vars.purchase_date || Date.now());
const daysSincePurchase = Math.floor((Date.now() - purchaseDate.getTime()) / 86400000);
const amount = parseFloat(vars.purchase_amount || '0');
const returnWindowDays = 30;
const authenticated = vars.customer_authenticated === 'true';
const reason = (vars.refund_reason || '').toLowerCase();

const withinWindow = daysSincePurchase <= returnWindowDays;
const isDefective = reason.includes('defect') || reason.includes('broken') || reason.includes('damaged');
const isWrongItem = reason.includes('wrong') || reason.includes('incorrect');

// Defective/wrong items eligible even outside window
const eligible = authenticated && (withinWindow || isDefective || isWrongItem);
const refundType = isDefective || isWrongItem ? 'full' : (daysSincePurchase <= 14 ? 'full' : 'partial');
const refundPercent = refundType === 'full' ? 100 : 85;
const refundAmount = (amount * refundPercent / 100).toFixed(2);
const needsReturn = !isDefective && amount > 25;

return {
  eligible: eligible ? 'yes' : 'no',
  days_since_purchase: daysSincePurchase,
  within_return_window: withinWindow ? 'yes' : 'no',
  refund_type: refundType,
  refund_percent: refundPercent,
  refund_amount: refundAmount,
  needs_return: needsReturn ? 'yes' : 'no',
  denial_reason: !authenticated ? 'Customer not authenticated' : (!withinWindow && !isDefective && !isWrongItem ? 'Outside 30-day return window' : '')
};`,
          outputVariables: [
            { name: "eligible", type: "string" },
            { name: "days_since_purchase", type: "number" },
            { name: "within_return_window", type: "string" },
            { name: "refund_type", type: "string" },
            { name: "refund_percent", type: "number" },
            { name: "refund_amount", type: "string" },
            { name: "needs_return", type: "string" },
            { name: "denial_reason", type: "string" },
          ],
        },
      }),

      // 4. Eligibility condition
      n("4", "condition", "Refund eligible?", 760, {
        conditionData: [
          {
            variable: "eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Refund amount calc (AI formatting)
      aiNode(
        "5",
        "AI format refund confirmation",
        1000,
        "You are a customer service refund specialist. Format a clear, friendly refund confirmation message for the chat channel. Include the refund amount, refund type (full/partial), whether the customer needs to return the item, and expected processing time (3-5 business days). Be empathetic and helpful. Respond JSON: {chat_response, refund_summary, processing_time}",
        "Customer: {customer_name}, Order: #{order_number}, Product: {product_name}, Original: ${purchase_amount}, Refund: ${refund_amount} ({refund_type}), Needs return: {needs_return}, Reason: {refund_reason}",
        [
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "refund_summary", as: "refund_summary", type: "string" },
          { field: "processing_time", as: "processing_time", type: "string" },
        ],
      ),

      // 6. Stripe refund initiation
      stripeNode(
        "6",
        "Initiate Stripe refund",
        1240,
        "POST",
        "refunds",
        [
          { key: "payment_intent", value: "{payment_intent_id}" },
          { key: "amount", value: "{refund_amount_cents}" },
          { key: "reason", value: "requested_by_customer" },
        ],
        [
          { name: "id", customName: "refund_id", type: "string" },
          { name: "status", customName: "refund_status", type: "string" },
          {
            name: "amount",
            customName: "refund_amount_confirmed",
            type: "number",
          },
        ],
      ),

      // 7. Return label generation
      n("7", "hitApi", "Generate return shipping label", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.easypost.com/v2/shipments",
          headers: [
            { key: "Authorization", value: "Bearer {easypost_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "shipment",
              value:
                '{"to_address":{"id":"{warehouse_address_id}"},"from_address":{"id":"{customer_address_id}"},"parcel":{"predefined_package":"FedExSmallBox"},"is_return":true}',
            },
          ],
          variables: [
            {
              name: "postage_label.label_url",
              customName: "return_label_url",
              type: "string",
            },
            {
              name: "tracking_code",
              customName: "return_tracking",
              type: "string",
            },
            {
              name: "selected_rate.rate",
              customName: "return_shipping_cost",
              type: "string",
            },
          ],
        },
      }),

      // 8. In-channel confirmation
      n("8", "sendMessage", "Send refund confirmation in chat", 1720, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{chat_response}\n\nRefund ID: {refund_id}\nStatus: {refund_status}\nAmount: ${refund_amount}\nProcessing: {processing_time}\n\nReturn label (if needed): {return_label_url}\nReturn tracking: {return_tracking}",
        },
      }),

      // 9. Finance escalation (Slack)
      slackNode(
        "9",
        "Slack: Finance escalation",
        1960,
        ":moneybag: Refund Processed\\nCustomer: {customer_name} ({customer_email})\\nOrder: #{order_number}\\nRefund: ${refund_amount} ({refund_type})\\nStripe Refund ID: {refund_id}\\nStatus: {refund_status}\\nReason: {refund_reason}\\nNeeds return: {needs_return}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Eligible -> process refund
      e("4", "9", "output_2"), // Not eligible -> finance escalation
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 496. REORDER REMINDER BASED ON PREDICTED NEXT ORDER (8 nodes)
  // ===================================================================

  "Reorder Reminder Based on Predicted Next Order": {
    summary:
      "Sends reorder reminders timed to ML-predicted next-order dates. A daily batch prediction job identifies customers approaching their predicted reorder window, enriches with item and complement data from Shopify, branches on engagement level, sends email or SMS reminders, logs prediction accuracy, and alerts on ML ops failures.",
    nodes: [
      trigger("Schedule: Daily prediction batch", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "A daily scheduled job at 6 AM runs ML predictions to identify customers whose predicted next-order date falls within the upcoming 5-day window",
        setup:
          "Configure your ML prediction model to output customer reorder predictions. The workflow processes customers whose predicted_next_order_date is within 5 days.",
        inputFields: [],
      }),

      // 2. Approaching window filter
      aiNode(
        "2",
        "AI filter approaching reorder window",
        280,
        "You are a predictive analytics specialist. Given the batch prediction results, identify customers whose predicted next order date falls within the next 5 days. For each customer, extract: email, name, predicted order date, confidence score (0-1), last order date, typical order items, average order value, engagement level (high/medium/low based on recent opens/clicks), and phone number if opted in for SMS. Respond JSON: {customer_email, customer_name, predicted_order_date, confidence_score, last_order_date, typical_items, avg_order_value, engagement_level, phone_number, sms_opted_in, days_until_predicted}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "predicted_order_date",
            as: "predicted_order_date",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "last_order_date", as: "last_order_date", type: "string" },
          { field: "typical_items", as: "typical_items", type: "string" },
          { field: "avg_order_value", as: "avg_order_value", type: "string" },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "sms_opted_in", as: "sms_opted_in", type: "string" },
          {
            field: "days_until_predicted",
            as: "days_until_predicted",
            type: "number",
          },
        ],
      ),

      // 3. Item + complement enrichment (Shopify)
      shopifyNode(
        "3",
        "Enrich with Shopify product data",
        520,
        "GET",
        "products.json?title={typical_items}",
        [],
        [
          {
            name: "products[0].title",
            customName: "primary_product",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "current_price",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "stock_level",
            type: "number",
          },
          {
            name: "products[0].images[0].src",
            customName: "product_image_url",
            type: "string",
          },
        ],
      ),

      // 4. Engagement condition
      n("4", "condition", "Customer engagement high?", 760, {
        conditionData: [
          {
            variable: "engagement_level",
            value: "low",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Reorder email
      emailNode(
        "5",
        "Reorder reminder email",
        1000,
        "{customer_email}",
        "Time to restock? Your {primary_product} might be running low",
        "Hi {customer_name},\\n\\nBased on your ordering pattern, we think you might be ready to restock soon (predicted: {predicted_order_date}).\\n\\nYour favorites:\\n{typical_items}\\n\\nCurrent price: ${current_price}\\nIn stock: {stock_level} units available\\n\\nReorder now: {reorder_link}\\n\\nAverage order: ${avg_order_value}\\n\\nDon't forget — add complementary items and save on shipping!",
      ),

      // 6. SMS for opted-in (Twilio)
      smsNode(
        "6",
        "SMS reorder reminder",
        1000,
        "{phone_number}",
        "Hi {customer_name}! Your {primary_product} might be running low. Reorder now: {reorder_link} - Save with auto-replenishment!",
        400,
      ),

      // 7. Prediction accuracy logging (Sheets)
      sheetsNode(
        "7",
        "Log prediction accuracy to Sheets",
        1240,
        "PredictionAccuracy",
        '[["{customer_email}","{predicted_order_date}","{confidence_score}","{last_order_date}","{days_until_predicted}","{engagement_level}","{primary_product}","{avg_order_value}","{sms_opted_in}"]]',
      ),

      // 8. ML ops failure alert
      slackNode(
        "8",
        "Slack: ML ops failure alert",
        1480,
        ":robot_face: Prediction Pipeline Alert\\nCustomer: {customer_email}\\nPredicted date: {predicted_order_date}\\nConfidence: {confidence_score}\\nEngagement: {engagement_level}\\nDays until predicted: {days_until_predicted}\\nReview prediction model accuracy and data pipeline health.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Engaged -> email
      e("4", "6", "output_2"), // Low engagement -> try SMS
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 497. REPEAT BROWSER RECOVERY (8 nodes)
  // ===================================================================

  "Repeat Browser Recovery": {
    summary:
      "Re-engages repeat browsers who visit multiple times without buying. A many-visit trigger compiles a product/category digest, uses AI to infer purchase barriers, branches on account status, sends loyalty-aware or first-purchase offer emails, tracks site returns, and logs threshold tuning data to Sheets.",
    nodes: [
      trigger("Webhook: Many-visit threshold hit", "webhook_app", {
        source: "Analytics Platform",
        event: "visitor.repeat_threshold",
        howItStarts:
          "A visitor hits the repeat-visit threshold (e.g., 5+ visits without purchase), triggering a targeted recovery flow with personalized product recommendations",
        setup:
          "Connect your analytics platform (Segment, Mixpanel, GA4) to fire a webhook when a visitor exceeds the repeat-visit threshold. Include browsing history and visitor data.",
        inputFields: [],
      }),

      // 2. Product/category digest compilation
      aiNode(
        "2",
        "AI compile browsing digest",
        280,
        "You are a behavioral analytics specialist. Parse the repeat visitor payload and extract: visitor email (if known), visitor name, visit count, pages viewed, top product categories browsed, specific products viewed with prices, time spent per visit, most recent visit date, has account (yes/no), loyalty tier if applicable, cart abandonment history. Create a curated product digest from their browsing. Respond JSON: {visitor_email, visitor_name, visit_count, top_categories, products_viewed, product_digest_html, avg_time_minutes, last_visit_date, has_account, loyalty_tier, cart_abandons, session_ids}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "visit_count", as: "visit_count", type: "number" },
          { field: "top_categories", as: "top_categories", type: "string" },
          { field: "products_viewed", as: "products_viewed", type: "string" },
          {
            field: "product_digest_html",
            as: "product_digest_html",
            type: "string",
          },
          { field: "avg_time_minutes", as: "avg_time_minutes", type: "string" },
          { field: "last_visit_date", as: "last_visit_date", type: "string" },
          { field: "has_account", as: "has_account", type: "string" },
          { field: "loyalty_tier", as: "loyalty_tier", type: "string" },
          { field: "cart_abandons", as: "cart_abandons", type: "number" },
        ],
      ),

      // 3. AI barrier inference
      aiNode(
        "3",
        "AI infer purchase barriers",
        520,
        "You are a conversion optimization specialist. Based on the visitor's repeat browsing behavior, infer the most likely purchase barriers: price sensitivity, decision paralysis, comparison shopping, waiting for sale, trust concerns, or shipping cost concerns. Recommend the best conversion tactic. Respond JSON: {primary_barrier, secondary_barrier, recommended_tactic, tactic_message, incentive_suggestion, urgency_hook}",
        "Visits: {visit_count}, Categories: {top_categories}, Products: {products_viewed}, Avg time: {avg_time_minutes} min, Has account: {has_account}, Loyalty: {loyalty_tier}, Cart abandons: {cart_abandons}",
        [
          { field: "primary_barrier", as: "primary_barrier", type: "string" },
          {
            field: "secondary_barrier",
            as: "secondary_barrier",
            type: "string",
          },
          {
            field: "recommended_tactic",
            as: "recommended_tactic",
            type: "string",
          },
          { field: "tactic_message", as: "tactic_message", type: "string" },
          {
            field: "incentive_suggestion",
            as: "incentive_suggestion",
            type: "string",
          },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
        ],
      ),

      // 4. Account status condition
      n("4", "condition", "Has existing account?", 760, {
        conditionData: [
          {
            variable: "has_account",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Loyalty-aware digest email (True: has account)
      emailNode(
        "5",
        "Loyalty-aware product digest email",
        1000,
        "{visitor_email}",
        "We noticed you browsing, {visitor_name} — here's your personalized picks",
        "Hi {visitor_name},\\n\\nYou've visited us {visit_count} times — we can tell you have great taste!\\n\\nHere's a curated selection based on what caught your eye:\\n{product_digest_html}\\n\\nAs a {loyalty_tier} member, you may qualify for exclusive pricing.\\n\\n{tactic_message}\\n\\n{urgency_hook}\\n\\nShop your picks: {shop_link}",
      ),

      // 6. First-purchase offer email (False: no account)
      emailNode(
        "6",
        "First-purchase offer email",
        1000,
        "{visitor_email}",
        "Still thinking about it? Here's a little something to help you decide",
        "Hi there,\\n\\nWe've seen you checking out our {top_categories} collection — {visit_count} visits and counting!\\n\\nHere's what caught your eye:\\n{product_digest_html}\\n\\n{tactic_message}\\n\\n{incentive_suggestion}\\n\\n{urgency_hook}\\n\\nShop now: {shop_link}",
        400,
      ),

      // 7. Site return tracking (runCode)
      n("7", "runCode", "Track site return conversion", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track repeat browser recovery metrics for optimization
const visitCount = parseInt(vars.visit_count || '0', 10);
const cartAbandons = parseInt(vars.cart_abandons || '0', 10);
const hasAccount = vars.has_account === 'yes';

return {
  recovery_attempt_timestamp: new Date().toISOString(),
  visitor_segment: visitCount >= 10 ? 'super_browser' : visitCount >= 5 ? 'repeat_browser' : 'early_browser',
  abandonment_rate: cartAbandons > 0 ? (cartAbandons / visitCount * 100).toFixed(1) : '0',
  account_status: hasAccount ? 'registered' : 'anonymous',
  barrier_addressed: vars.primary_barrier || 'unknown',
  tactic_used: vars.recommended_tactic || 'unknown'
};`,
          outputVariables: [
            { name: "recovery_attempt_timestamp", type: "string" },
            { name: "visitor_segment", type: "string" },
            { name: "abandonment_rate", type: "string" },
            { name: "account_status", type: "string" },
            { name: "barrier_addressed", type: "string" },
            { name: "tactic_used", type: "string" },
          ],
        },
      }),

      // 8. Threshold tuning logging (Sheets)
      sheetsNode(
        "8",
        "Log threshold tuning to Sheets",
        1480,
        "BrowserRecovery",
        '[["{visitor_email}","{visit_count}","{visitor_segment}","{top_categories}","{primary_barrier}","{recommended_tactic}","{has_account}","{loyalty_tier}","{cart_abandons}","{abandonment_rate}","{recovery_attempt_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Has account -> loyalty-aware digest
      e("4", "6", "output_2"), // No account -> first-purchase offer
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 498. REPEAT BUYER LAPSED (8 nodes)
  // ===================================================================

  "Repeat Buyer Lapsed": {
    summary:
      "Reactivates lapsed repeat buyers with tier-appropriate win-back. A lapse threshold trigger enriches the customer from HubSpot, generates an AI win-back offer, branches on VIP status, creates a high-touch task or sends a lifecycle email, follows up with SMS for non-openers, and logs reactivation data to Sheets.",
    nodes: [
      trigger("Webhook: Repeat buyer lapse threshold", "webhook_app", {
        source: "E-commerce Platform",
        event: "customer.lapse_threshold",
        howItStarts:
          "A previously active repeat buyer crosses the lapse threshold (no purchase in 60+ days when their typical order frequency is every 30 days), triggering a tiered win-back flow",
        setup:
          "Connect your e-commerce platform to fire a webhook when a repeat buyer's days-since-last-order exceeds 2x their average order frequency. Include customer history data.",
        inputFields: [],
      }),

      // 2. Loyalty/spend enrichment (HubSpot)
      hubspotNode(
        "2",
        "Enrich from HubSpot CRM",
        280,
        "GET",
        "contacts/{customer_hubspot_id}?properties=email,firstname,lastname,total_revenue,num_orders,lifecycle_stage,hs_lead_status",
        [],
        [
          {
            name: "properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "total_revenue",
            type: "string",
          },
          {
            name: "properties.num_orders",
            customName: "total_orders",
            type: "number",
          },
          {
            name: "properties.lifecycle_stage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 3. AI win-back calculation
      aiNode(
        "3",
        "AI calculate win-back strategy",
        520,
        "You are a customer retention strategist. Given the lapsed repeat buyer data, determine the optimal win-back strategy. Calculate VIP status based on total revenue and order count. For VIP customers (>$500 LTV or >10 orders), recommend personal outreach. For standard customers, recommend automated lifecycle email. Generate a personalized win-back offer with appropriate discount level. Respond JSON: {is_vip, vip_reason, win_back_discount, offer_message, subject_line, sms_message, days_lapsed, avg_order_frequency, urgency_level, phone_number, sms_opted_in}",
        "Email: {customer_email}, Name: {first_name} {last_name}, Revenue: ${total_revenue}, Orders: {total_orders}, Stage: {lifecycle_stage}, Status: {lead_status}",
        [
          { field: "is_vip", as: "is_vip", type: "string" },
          { field: "vip_reason", as: "vip_reason", type: "string" },
          {
            field: "win_back_discount",
            as: "win_back_discount",
            type: "string",
          },
          { field: "offer_message", as: "offer_message", type: "string" },
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "sms_message", as: "sms_message", type: "string" },
          { field: "days_lapsed", as: "days_lapsed", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "phone_number", as: "phone_number", type: "string" },
          { field: "sms_opted_in", as: "sms_opted_in", type: "string" },
        ],
      ),

      // 4. VIP condition
      n("4", "condition", "Customer is VIP?", 760, {
        conditionData: [
          {
            variable: "is_vip",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. High-touch task creation (HubSpot)
      hubspotNode(
        "5",
        "Create VIP win-back task in HubSpot",
        1000,
        "POST",
        "objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"VIP Win-Back: {first_name} {last_name} (${total_revenue} LTV)","hs_task_body":"Lapsed VIP customer needs personal outreach.\\nReason: {vip_reason}\\nDays lapsed: {days_lapsed}\\nSuggested offer: {win_back_discount} discount\\nTotal orders: {total_orders}\\nUrgency: {urgency_level}","hs_task_status":"NOT_STARTED","hs_task_priority":"HIGH"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_status",
            customName: "task_status",
            type: "string",
          },
        ],
      ),

      // 6. Lifecycle email (non-VIP)
      emailNode(
        "6",
        "Lifecycle win-back email",
        1000,
        "{customer_email}",
        "{subject_line}",
        "Hi {first_name},\\n\\nWe miss you! It's been {days_lapsed} days since your last order, and we want to make sure everything's okay.\\n\\n{offer_message}\\n\\nAs a thank you for being a loyal customer ({total_orders} orders!), here's an exclusive {win_back_discount} off your next purchase.\\n\\nShop now: {reorder_link}\\n\\nThis offer expires in 7 days — don't miss out!",
        400,
      ),

      // 7. SMS for non-openers (Twilio) — delayed
      n("7", "delayAction", "Wait 3 days for email open", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 3, delayUnit: "days" },
      }),

      // 8. Reactivation logging (Sheets)
      sheetsNode(
        "8",
        "Log reactivation to Sheets",
        1480,
        "LapsedBuyerReactivation",
        '[["{customer_email}","{first_name} {last_name}","{total_revenue}","{total_orders}","{is_vip}","{days_lapsed}","{win_back_discount}","{urgency_level}","{lifecycle_stage}","{sms_opted_in}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP -> high-touch task
      e("4", "6", "output_2"), // Standard -> lifecycle email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 499. REPLACEMENT PART ORDERING BOT (8 nodes)
  // ===================================================================

  "Replacement Part Ordering Bot": {
    summary:
      "Helps customers find and order compatible replacement parts via chat. A chat trigger identifies the original product, pulls compatible parts from Shopify, checks availability, generates an AI compatibility response, provides add-to-cart or waitlist links, delivers the response in the chat channel, and alerts parts support on Slack.",
    nodes: [
      trigger("Webhook: Chat replacement part request", "webhook_app", {
        source: "Chat Platform",
        event: "message.replacement_part_intent",
        howItStarts:
          "A customer asks about replacement parts for a product they own, triggering an automated parts lookup and compatibility check",
        setup:
          "Connect your chat platform to detect replacement part intent and fire a webhook. Include the conversation ID and customer message with product details.",
        inputFields: [],
      }),

      // 2. Original product identification
      aiNode(
        "2",
        "AI identify original product",
        280,
        "You are a product compatibility specialist. Parse the customer's replacement part request and identify: conversation ID, customer email, customer name, the original product they own (name, model, SKU if mentioned), the specific part they need, any serial number or purchase date mentioned, and urgency level. Respond JSON: {conversation_id, customer_email, customer_name, original_product, original_model, original_sku, part_needed, serial_number, purchase_date, urgency}",
        "{webhook_payload}",
        [
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "original_product", as: "original_product", type: "string" },
          { field: "original_model", as: "original_model", type: "string" },
          { field: "original_sku", as: "original_sku", type: "string" },
          { field: "part_needed", as: "part_needed", type: "string" },
          { field: "serial_number", as: "serial_number", type: "string" },
          { field: "purchase_date", as: "purchase_date", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Compatible parts pull (Shopify)
      shopifyNode(
        "3",
        "Look up compatible parts in Shopify",
        520,
        "GET",
        "products.json?product_type=replacement_part&tag={original_sku}",
        [],
        [
          {
            name: "products[0].title",
            customName: "part_1_title",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "part_1_price",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "part_1_stock",
            type: "number",
          },
          {
            name: "products[0].variants[0].id",
            customName: "part_1_variant_id",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "part_2_title",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "part_2_price",
            type: "string",
          },
          {
            name: "products[1].variants[0].inventory_quantity",
            customName: "part_2_stock",
            type: "number",
          },
        ],
      ),

      // 4. Availability condition
      n("4", "condition", "Compatible parts in stock?", 760, {
        conditionData: [
          {
            variable: "part_1_stock",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI compatibility response
      aiNode(
        "5",
        "AI generate compatibility response",
        1000,
        "You are a helpful parts advisor. Generate a friendly, clear chat response listing the compatible replacement parts found. Include part names, prices, stock status, and a direct add-to-cart link. If parts are out of stock, offer a waitlist option. Be empathetic about the customer's need for a replacement. Respond JSON: {chat_response, parts_summary, has_in_stock, add_to_cart_url, waitlist_url}",
        "Customer: {customer_name}, Original: {original_product} ({original_model}), Part needed: {part_needed}, Urgency: {urgency}, Part 1: {part_1_title} (${part_1_price}, stock: {part_1_stock}), Part 2: {part_2_title} (${part_2_price}, stock: {part_2_stock})",
        [
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "parts_summary", as: "parts_summary", type: "string" },
          { field: "has_in_stock", as: "has_in_stock", type: "string" },
          { field: "add_to_cart_url", as: "add_to_cart_url", type: "string" },
          { field: "waitlist_url", as: "waitlist_url", type: "string" },
        ],
      ),

      // 6. Add-to-cart/waitlist link (runCode)
      n("6", "runCode", "Build cart/waitlist links", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build add-to-cart or waitlist links based on stock
const inStock = parseInt(vars.part_1_stock || '0', 10) > 0;
const variantId = vars.part_1_variant_id || '';
const cartUrl = inStock ? 'https://shop.example.com/cart/add?id=' + variantId + '&quantity=1' : '';
const waitlistUrl = !inStock ? 'https://shop.example.com/waitlist?product=' + encodeURIComponent(vars.part_1_title || '') : '';

return {
  action_url: inStock ? cartUrl : waitlistUrl,
  action_type: inStock ? 'add_to_cart' : 'waitlist',
  action_label: inStock ? 'Add to Cart' : 'Join Waitlist'
};`,
          outputVariables: [
            { name: "action_url", type: "string" },
            { name: "action_type", type: "string" },
            { name: "action_label", type: "string" },
          ],
        },
      }),

      // 7. Channel delivery
      n("7", "sendMessage", "Deliver parts response in chat", 1480, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{chat_response}\n\n{action_label}: {action_url}\n\nParts summary: {parts_summary}",
        },
      }),

      // 8. Parts support Slack alert
      slackNode(
        "8",
        "Slack: Parts support alert",
        1720,
        ":wrench: Replacement Part Request\\nCustomer: {customer_name} ({customer_email})\\nOriginal: {original_product} ({original_model})\\nPart needed: {part_needed}\\nFound: {part_1_title} (${part_1_price}, stock: {part_1_stock})\\nAction: {action_type}\\nUrgency: {urgency}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // In stock -> compatibility response with cart link
      e("4", "5", "output_2"), // Out of stock -> compatibility response with waitlist
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 500. REPLACEMENT-PART REORDER (7 nodes)
  // ===================================================================

  "Replacement-Part Reorder": {
    summary:
      "Proactively reminds customers to reorder replacement parts based on component lifespan. A product delivery triggers lifespan reminder calculation via runCode, waits until the predicted replacement window, checks for suppression, sends a compatible-part email via Postmark, and alerts ops on failures.",
    nodes: [
      trigger("Webhook: Product delivered", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A product with replaceable components is delivered, starting the lifespan countdown for proactive replacement-part reminders",
        setup:
          "Connect Shopify fulfillments/create webhook. Tag products with replaceable components and their expected lifespan in product metafields.",
        inputFields: [],
      }),

      // 2. Lifespan reminder calc (runCode)
      n("2", "runCode", "Calculate part lifespan reminder", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate replacement part reminder timing based on component lifespan
const payload = JSON.parse(vars.webhook_payload || '{}');
const productTitle = payload.line_items?.[0]?.title || 'Unknown Product';
const productId = payload.line_items?.[0]?.product_id || '';
const sku = payload.line_items?.[0]?.sku || '';
const email = payload.email || payload.customer?.email || '';
const firstName = payload.customer?.first_name || 'Customer';

// Component lifespan lookup (days) by product category
const lifespanMap = {
  'filter': 90, 'brush_head': 90, 'blade': 60, 'cartridge': 45,
  'battery': 365, 'belt': 180, 'pad': 60, 'bulb': 365, 'default': 90
};
const productLower = productTitle.toLowerCase();
const matchedType = Object.keys(lifespanMap).find(k => productLower.includes(k)) || 'default';
const lifespanDays = lifespanMap[matchedType];
const reminderDays = Math.max(1, lifespanDays - 14); // remind 2 weeks before end of life

return {
  customer_email: email,
  customer_first_name: firstName,
  product_title: productTitle,
  product_id: productId,
  sku: sku,
  component_type: matchedType,
  lifespan_days: lifespanDays,
  reminder_delay_days: reminderDays,
  replacement_date: new Date(Date.now() + lifespanDays * 86400000).toISOString().split('T')[0],
  reminder_date: new Date(Date.now() + reminderDays * 86400000).toISOString().split('T')[0]
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "product_id", type: "string" },
            { name: "sku", type: "string" },
            { name: "component_type", type: "string" },
            { name: "lifespan_days", type: "number" },
            { name: "reminder_delay_days", type: "number" },
            { name: "replacement_date", type: "string" },
            { name: "reminder_date", type: "string" },
          ],
        },
      }),

      // 3. Scheduled delay
      n("3", "delayAction", "Wait until replacement window", 520, {
        delayDetails: {
          delayType: "dynamic",
          delayField: "reminder_delay_days",
          delayUnit: "days",
        },
      }),

      // 4. Reorder suppression check condition
      n("4", "condition", "Customer not already reordered?", 760, {
        conditionData: [
          {
            variable: "customer_email",
            value: "",
            comparison_operator: "NOT EMPTY",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Compatible part email
      emailNode(
        "5",
        "Replacement part reminder email",
        1000,
        "{customer_email}",
        "Time for a new {component_type} for your {product_title}",
        "Hi {customer_first_name},\\n\\nYour {product_title} has been working hard for about {lifespan_days} days, and it's almost time to replace the {component_type}.\\n\\nFresh {component_type} replacements keep your {product_title} performing at its best.\\n\\nShop compatible {component_type} replacements: {replacement_parts_link}\\n\\nPro tip: Subscribe for automatic replacements and save 15% on every order.\\n\\nKeep things running smoothly!",
      ),

      // 6. Send via Postmark
      postmarkNode(
        "6",
        "Send via Postmark",
        1240,
        "{customer_email}",
        "Your {product_title} {component_type} is due for replacement",
        "<h2>Time for a fresh {component_type}!</h2><p>Hi {customer_first_name}, your {product_title}'s {component_type} has reached its expected lifespan of {lifespan_days} days.</p><p><a href='{replacement_parts_link}'>Shop Compatible Replacements</a></p><p>Subscribe & save 15% on recurring replacements.</p>",
        "outbound",
      ),

      // 7. Ops failure alert (Slack)
      slackNode(
        "7",
        "Slack: Ops failure alert",
        1480,
        ":warning: Replacement Part Reminder Issue\\nProduct: {product_title} (SKU: {sku})\\nComponent: {component_type}\\nCustomer: {customer_email}\\nLifespan: {lifespan_days} days\\nReplacement date: {replacement_date}\\nReview reminder delivery status.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Not reordered -> send reminder
      e("4", "7", "output_2"), // Already reordered or invalid -> ops alert
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 501. REPLENISHMENT AFTER NEGATIVE STOCK EVENT (8 nodes)
  // ===================================================================

  "Replenishment After Negative Stock Event": {
    summary:
      "Notifies backorder and waitlist customers when stock is replenished after a stockout. A restock trigger identifies backorder and waitlist customers, compares demand vs available stock via runCode, sends priority emails to backorder customers, batch-notifies the waitlist, sends SMS to priority customers via Twilio, logs stock-to-waitlist data, and alerts inventory ops on Slack.",
    nodes: [
      trigger("Webhook: Product restocked", "webhook_app", {
        source: "Inventory System",
        event: "inventory.restocked",
        howItStarts:
          "A previously out-of-stock product is restocked, triggering notifications to backorder customers and waitlisted shoppers",
        setup:
          "Connect your inventory system to fire a webhook when stock is received for a previously zero-stock SKU. Include product details, quantity received, and backorder/waitlist counts.",
        inputFields: [],
      }),

      // 2. Backorder + waitlist identification
      aiNode(
        "2",
        "AI extract restock + waitlist data",
        280,
        "You are an inventory operations specialist. Parse the restock webhook payload and extract: product name, SKU, quantity restocked, total backorder count, total waitlist count, backorder customer emails (comma-separated), waitlist customer emails (comma-separated), unit price, product category, warehouse location, and estimated fulfillment date for backorders. Respond JSON: {product_name, sku, qty_restocked, backorder_count, waitlist_count, backorder_emails, waitlist_emails, unit_price, product_category, warehouse, fulfillment_date, priority_phone_numbers}",
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "sku", as: "sku", type: "string" },
          { field: "qty_restocked", as: "qty_restocked", type: "number" },
          { field: "backorder_count", as: "backorder_count", type: "number" },
          { field: "waitlist_count", as: "waitlist_count", type: "number" },
          { field: "backorder_emails", as: "backorder_emails", type: "string" },
          { field: "waitlist_emails", as: "waitlist_emails", type: "string" },
          { field: "unit_price", as: "unit_price", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "warehouse", as: "warehouse", type: "string" },
          { field: "fulfillment_date", as: "fulfillment_date", type: "string" },
          {
            field: "priority_phone_numbers",
            as: "priority_phone_numbers",
            type: "string",
          },
        ],
      ),

      // 3. Demand vs stock comparison (runCode)
      n("3", "runCode", "Compare demand vs available stock", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare restocked quantity against backorder + waitlist demand
const restocked = parseInt(vars.qty_restocked || '0', 10);
const backorderCount = parseInt(vars.backorder_count || '0', 10);
const waitlistCount = parseInt(vars.waitlist_count || '0', 10);
const totalDemand = backorderCount + waitlistCount;
const fulfillableBackorders = Math.min(backorderCount, restocked);
const remainingAfterBackorders = Math.max(0, restocked - backorderCount);
const fulfillableWaitlist = Math.min(waitlistCount, remainingAfterBackorders);
const unfulfilled = Math.max(0, totalDemand - restocked);

return {
  total_demand: totalDemand,
  fulfillable_backorders: fulfillableBackorders,
  fulfillable_waitlist: fulfillableWaitlist,
  remaining_stock: Math.max(0, restocked - totalDemand),
  unfulfilled_count: unfulfilled,
  demand_exceeds_stock: totalDemand > restocked ? 'yes' : 'no',
  stock_coverage_percent: totalDemand > 0 ? Math.round(restocked / totalDemand * 100) : 100
};`,
          outputVariables: [
            { name: "total_demand", type: "number" },
            { name: "fulfillable_backorders", type: "number" },
            { name: "fulfillable_waitlist", type: "number" },
            { name: "remaining_stock", type: "number" },
            { name: "unfulfilled_count", type: "number" },
            { name: "demand_exceeds_stock", type: "string" },
            { name: "stock_coverage_percent", type: "number" },
          ],
        },
      }),

      // 4. Backorder priority email
      emailNode(
        "4",
        "Backorder fulfillment email",
        760,
        "{backorder_emails}",
        "Great news! Your backordered {product_name} is shipping soon",
        "Hello,\\n\\nYour backordered {product_name} (SKU: {sku}) is now back in stock and your order is being fulfilled!\\n\\nEstimated shipping: {fulfillment_date}\\nPrice: ${unit_price}\\n\\nWe'll send tracking information once your order ships.\\n\\nThank you for your patience!",
      ),

      // 5. Waitlist batch notification
      emailNode(
        "5",
        "Waitlist notification email",
        1000,
        "{waitlist_emails}",
        "{product_name} is back in stock — get yours before it sells out!",
        "Hello,\\n\\nThe wait is over! {product_name} (SKU: {sku}) is back in stock.\\n\\nQuantity available: {qty_restocked} units (high demand — {waitlist_count} people are waiting)\\nPrice: ${unit_price}\\n\\nShop now before it sells out again: {product_link}\\n\\nAct fast — first come, first served!",
      ),

      // 6. SMS for priority (Twilio)
      smsNode(
        "6",
        "SMS priority restock notification",
        1240,
        "{priority_phone_numbers}",
        "{product_name} is BACK IN STOCK! Your backorder is being shipped. Estimated delivery: {fulfillment_date}. Questions? Reply to this text.",
      ),

      // 7. Stock-to-waitlist logging (Sheets)
      sheetsNode(
        "7",
        "Log stock vs waitlist to Sheets",
        1480,
        "RestockNotifications",
        '[["{product_name}","{sku}","{qty_restocked}","{backorder_count}","{waitlist_count}","{total_demand}","{fulfillable_backorders}","{fulfillable_waitlist}","{unfulfilled_count}","{demand_exceeds_stock}","{stock_coverage_percent}","{warehouse}"]]',
      ),

      // 8. Inventory ops alert (Slack)
      slackNode(
        "8",
        "Slack: Inventory ops restock alert",
        1720,
        ":package: Restock Notification Sent\\nProduct: {product_name} ({sku})\\nRestocked: {qty_restocked} units\\nBackorders: {backorder_count} (fulfilling: {fulfillable_backorders})\\nWaitlist: {waitlist_count} (fulfilling: {fulfillable_waitlist})\\nUnfulfilled: {unfulfilled_count}\\nDemand exceeds stock: {demand_exceeds_stock}\\nWarehouse: {warehouse}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 502. REPLENISHMENT REMINDER ON FIXED INTERVAL (7 nodes)
  // ===================================================================

  "Replenishment Reminder on Fixed Interval": {
    summary:
      "Sends replenishment reminders on a fixed interval after consumable delivery. A consumable delivery triggers interval calculation via runCode, waits the fixed period, checks if the customer has already reordered, sends the replenishment email via Postmark, alerts ops on failures, and logs the interval audit.",
    nodes: [
      trigger("Webhook: Consumable product delivered", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A consumable product is delivered, starting a fixed-interval countdown for replenishment reminders",
        setup:
          "Connect Shopify fulfillments/create webhook. Tag consumable products with their replenishment interval in product metafields (e.g., 30, 60, 90 days).",
        inputFields: [],
      }),

      // 2. Fixed interval calc (runCode)
      n("2", "runCode", "Calculate fixed replenishment interval", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate fixed replenishment interval from product data
const payload = JSON.parse(vars.webhook_payload || '{}');
const productTitle = payload.line_items?.[0]?.title || 'Unknown Product';
const sku = payload.line_items?.[0]?.sku || '';
const email = payload.email || payload.customer?.email || '';
const firstName = payload.customer?.first_name || 'Customer';
const quantity = parseInt(payload.line_items?.[0]?.quantity || '1', 10);

// Fixed interval lookup by category keyword
const intervals = {
  'vitamin': 30, 'supplement': 30, 'protein': 28, 'coffee': 14,
  'tea': 21, 'skincare': 45, 'shampoo': 60, 'toothpaste': 90,
  'razor': 30, 'contact': 30, 'pet food': 21, 'default': 30
};
const titleLower = productTitle.toLowerCase();
const matchedKey = Object.keys(intervals).find(k => titleLower.includes(k)) || 'default';
const intervalDays = intervals[matchedKey] * quantity;
const reminderDays = Math.max(1, intervalDays - 5);

return {
  customer_email: email,
  customer_first_name: firstName,
  product_title: productTitle,
  sku: sku,
  quantity: quantity,
  interval_days: intervalDays,
  reminder_delay_days: reminderDays,
  next_replenishment_date: new Date(Date.now() + intervalDays * 86400000).toISOString().split('T')[0],
  interval_category: matchedKey
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "sku", type: "string" },
            { name: "quantity", type: "number" },
            { name: "interval_days", type: "number" },
            { name: "reminder_delay_days", type: "number" },
            { name: "next_replenishment_date", type: "string" },
            { name: "interval_category", type: "string" },
          ],
        },
      }),

      // 3. Scheduled delay
      n("3", "delayAction", "Wait fixed interval", 520, {
        delayDetails: {
          delayType: "dynamic",
          delayField: "reminder_delay_days",
          delayUnit: "days",
        },
      }),

      // 4. Reorder check condition
      n("4", "condition", "Customer has not reordered?", 760, {
        conditionData: [
          {
            variable: "customer_email",
            value: "",
            comparison_operator: "NOT EMPTY",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Replenishment email (Postmark)
      postmarkNode(
        "5",
        "Send replenishment via Postmark",
        1000,
        "{customer_email}",
        "Time to restock your {product_title}!",
        "<h2>Running low on {product_title}?</h2><p>Hi {customer_first_name}, it's been about {interval_days} days since your last order of {quantity}x {product_title}.</p><p>Estimated depletion: {next_replenishment_date}</p><p><a href='{reorder_link}'>Reorder Now</a></p><p>Subscribe and save 10% on every delivery!</p>",
        "outbound",
      ),

      // 6. Ops failure alert (Slack)
      slackNode(
        "6",
        "Slack: Ops replenishment alert",
        1240,
        ":warning: Fixed Interval Replenishment Alert\\nProduct: {product_title} (SKU: {sku})\\nCustomer: {customer_email}\\nInterval: {interval_days} days ({interval_category})\\nNext replenishment: {next_replenishment_date}\\nReview delivery status.",
        400,
      ),

      // 7. Interval audit
      sheetsNode(
        "7",
        "Log interval audit to Sheets",
        1480,
        "ReplenishmentAudit",
        '[["{customer_email}","{product_title}","{sku}","{quantity}","{interval_days}","{interval_category}","{next_replenishment_date}","{reminder_delay_days}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Not reordered -> send reminder
      e("4", "6", "output_2"), // Already reordered or invalid -> ops alert
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 503. REPLENISHMENT BY PREDICTED NEXT-ORDER DATE (8 nodes)
  // ===================================================================

  "Replenishment by Predicted Next-Order Date": {
    summary:
      "Uses ML confidence scoring to time replenishment reminders. A daily ML scoring job filters on confidence threshold, enriches product data from Shopify, branches on confidence band, sends assertive reorder emails for high-confidence or gentle suggestions for lower-confidence, logs conversion data, and alerts on ML ops issues.",
    nodes: [
      trigger("Schedule: Daily ML scoring run", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "A daily scheduled job at 7 AM scores each customer's predicted next-order date using the replenishment ML model and identifies customers within the reorder window",
        setup:
          "Configure your ML model to output predictions with confidence scores. The workflow filters on confidence >= 0.6 and processes customers whose predicted date is within 7 days.",
        inputFields: [],
      }),

      // 2. Confidence filter
      aiNode(
        "2",
        "AI filter by confidence threshold",
        280,
        "You are a machine learning operations specialist. Parse the ML prediction batch results and filter to customers with confidence >= 0.6 whose predicted next-order date is within 7 days. Extract: customer email, customer name, predicted order date, confidence score, last ordered product, product SKU, last order amount, confidence band (high >= 0.85, medium 0.7-0.84, low 0.6-0.69), days until predicted order, and phone number. Respond JSON: {customer_email, customer_name, predicted_date, confidence_score, confidence_band, last_product, product_sku, last_amount, days_until_predicted, phone_number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "predicted_date", as: "predicted_date", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "confidence_band", as: "confidence_band", type: "string" },
          { field: "last_product", as: "last_product", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          { field: "last_amount", as: "last_amount", type: "string" },
          {
            field: "days_until_predicted",
            as: "days_until_predicted",
            type: "number",
          },
          { field: "phone_number", as: "phone_number", type: "string" },
        ],
      ),

      // 3. Product enrichment (Shopify)
      shopifyNode(
        "3",
        "Enrich product from Shopify",
        520,
        "GET",
        "products.json?ids={product_sku}",
        [],
        [
          {
            name: "products[0].title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "current_price",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "stock_available",
            type: "number",
          },
          {
            name: "products[0].images[0].src",
            customName: "product_image",
            type: "string",
          },
        ],
      ),

      // 4. Confidence band condition
      n("4", "condition", "High confidence prediction?", 760, {
        conditionData: [
          {
            variable: "confidence_band",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Assertive reorder email (True: high confidence)
      emailNode(
        "5",
        "Assertive reorder email",
        1000,
        "{customer_email}",
        "Your {product_title} is running low — reorder now!",
        "Hi {customer_name},\\n\\nBased on your ordering pattern, you're likely running low on {product_title} right about now.\\n\\nReorder with one click: {reorder_link}\\n\\nCurrent price: ${current_price}\\nIn stock: {stock_available} units\\n\\nDon't wait until you run out — reorder today and enjoy uninterrupted supply!\\n\\nSubscribe & save 10% on every delivery.",
      ),

      // 6. Gentle suggestion email (False: medium/low confidence)
      emailNode(
        "6",
        "Gentle suggestion email",
        1000,
        "{customer_email}",
        "Might be time to check your {product_title} supply",
        "Hi {customer_name},\\n\\nJust a friendly reminder — it's been a while since your last {product_title} order, and we thought you might want to stock up.\\n\\nBrowse {product_title}: {product_link}\\nCurrent price: ${current_price}\\n\\nNo rush — we just want to make sure you don't run out when you need it!\\n\\nSet up auto-replenishment to never worry about reordering again.",
        400,
      ),

      // 7. Conversion logging (Sheets)
      sheetsNode(
        "7",
        "Log prediction + conversion to Sheets",
        1240,
        "PredictedReplenishment",
        '[["{customer_email}","{customer_name}","{predicted_date}","{confidence_score}","{confidence_band}","{product_title}","{product_sku}","{current_price}","{days_until_predicted}","{stock_available}"]]',
      ),

      // 8. ML ops alert
      slackNode(
        "8",
        "Slack: ML ops alert",
        1480,
        ":robot_face: Replenishment Prediction Alert\\nCustomer: {customer_email}\\nProduct: {product_title} ({product_sku})\\nPredicted date: {predicted_date}\\nConfidence: {confidence_score} ({confidence_band})\\nDays until: {days_until_predicted}\\nStock: {stock_available}\\nMonitor prediction accuracy.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // High confidence -> assertive email
      e("4", "6", "output_2"), // Medium/low -> gentle suggestion
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 504. REPLENISHMENT WITH QUANTITY BRANCH (7 nodes)
  // ===================================================================

  "Replenishment with Quantity Branch": {
    summary:
      "Branches replenishment reminders by purchase quantity tier. A consumable delivery triggers quantity and pack classification via runCode, branches on bulk vs single, sends extended-interval emails for bulk buyers or subscribe-and-save emails for single buyers, optimizes send timing, and logs quantity tier data to Sheets.",
    nodes: [
      trigger("Webhook: Consumable delivered", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A consumable product is delivered, triggering quantity-aware replenishment logic that adjusts timing and messaging based on the amount purchased",
        setup:
          "Connect Shopify fulfillments/create webhook. The workflow will classify the purchase as bulk or single and adjust replenishment timing accordingly.",
        inputFields: [],
      }),

      // 2. Quantity/pack classification (runCode)
      n("2", "runCode", "Classify quantity tier", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify purchase quantity into bulk vs single tier
const payload = JSON.parse(vars.webhook_payload || '{}');
const productTitle = payload.line_items?.[0]?.title || 'Unknown';
const sku = payload.line_items?.[0]?.sku || '';
const quantity = parseInt(payload.line_items?.[0]?.quantity || '1', 10);
const email = payload.email || payload.customer?.email || '';
const firstName = payload.customer?.first_name || 'Customer';
const price = parseFloat(payload.line_items?.[0]?.price || '0');

// Detect bulk: multi-pack, large quantity, or "bulk" in title
const isBulk = quantity >= 3 || productTitle.toLowerCase().includes('bulk') || productTitle.toLowerCase().includes('pack') || productTitle.toLowerCase().includes('bundle');
const baseIntervalDays = 30;
const intervalMultiplier = isBulk ? quantity : 1;
const intervalDays = baseIntervalDays * intervalMultiplier;
const reminderDays = Math.max(1, intervalDays - 7);
const savingsPercent = isBulk ? 0 : 15; // Single buyers get subscribe-and-save offer

return {
  customer_email: email,
  customer_first_name: firstName,
  product_title: productTitle,
  sku: sku,
  quantity: quantity,
  unit_price: price.toFixed(2),
  is_bulk: isBulk ? 'yes' : 'no',
  quantity_tier: isBulk ? 'bulk' : 'single',
  interval_days: intervalDays,
  reminder_delay_days: reminderDays,
  next_replenishment: new Date(Date.now() + intervalDays * 86400000).toISOString().split('T')[0],
  subscribe_savings_percent: savingsPercent
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "sku", type: "string" },
            { name: "quantity", type: "number" },
            { name: "unit_price", type: "string" },
            { name: "is_bulk", type: "string" },
            { name: "quantity_tier", type: "string" },
            { name: "interval_days", type: "number" },
            { name: "reminder_delay_days", type: "number" },
            { name: "next_replenishment", type: "string" },
            { name: "subscribe_savings_percent", type: "number" },
          ],
        },
      }),

      // 3. Bulk vs single condition
      n("3", "condition", "Bulk purchase?", 520, {
        conditionData: [
          {
            variable: "is_bulk",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Bulk extended-interval email (True)
      emailNode(
        "4",
        "Bulk buyer extended-interval email",
        760,
        "{customer_email}",
        "Your {product_title} bulk supply — we'll remind you in {interval_days} days",
        "Hi {customer_first_name},\\n\\nThanks for stocking up with {quantity}x {product_title}!\\n\\nWith your bulk purchase, you should be set for about {interval_days} days (estimated depletion: {next_replenishment}).\\n\\nWe'll send you a reminder when it's time to restock. In the meantime, enjoy your supply!\\n\\nBulk buyers tip: Your bulk order saved you on per-unit costs. Keep it up!\\n\\nNeed anything sooner? Reorder anytime: {reorder_link}",
      ),

      // 5. Single subscribe-and-save email (False)
      emailNode(
        "5",
        "Single buyer subscribe-and-save email",
        760,
        "{customer_email}",
        "Save {subscribe_savings_percent}% on {product_title} with Subscribe & Save",
        "Hi {customer_first_name},\\n\\nEnjoying your {product_title}? Since you ordered a single unit, you'll likely need a refill in about {interval_days} days.\\n\\nNever run out again — Subscribe & Save:\\n- Save {subscribe_savings_percent}% on every delivery\\n- Choose your frequency (every {interval_days} days recommended)\\n- Skip or cancel anytime\\n\\nSubscribe now: {subscribe_link}\\n\\nOr, save with a bulk order next time: {bulk_link}",
        400,
      ),

      // 6. Send timing
      n("6", "delayAction", "Optimal send timing delay", 1000, {
        delayDetails: { delayType: "fixed", delayValue: 2, delayUnit: "hours" },
      }),

      // 7. Quantity tier logging (Sheets)
      sheetsNode(
        "7",
        "Log quantity tier to Sheets",
        1240,
        "QuantityTierReplenishment",
        '[["{customer_email}","{product_title}","{sku}","{quantity}","{quantity_tier}","{interval_days}","{next_replenishment}","{unit_price}","{subscribe_savings_percent}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Bulk -> extended interval email
      e("3", "5", "output_2"), // Single -> subscribe & save email
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 505. REPLENISHMENT WITH REVIEW REQUEST FIRST (8 nodes)
  // ===================================================================

  "Replenishment with Review Request First": {
    summary:
      "Sequences a review request before the replenishment reminder for better engagement. A consumable delivery triggers an experience delay, sends a review request email, waits until depletion timing, checks if the customer left a review, sends a thank-you-plus-reorder or combined review-plus-reorder email, and logs sequence performance.",
    nodes: [
      trigger("Webhook: Consumable delivered", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A consumable product is delivered, triggering a two-phase sequence: first a review request after initial experience, then a replenishment reminder timed to depletion",
        setup:
          "Connect Shopify fulfillments/create webhook. The workflow sends a review request first, waits for depletion, then sends a replenishment reminder with review-aware messaging.",
        inputFields: [],
      }),

      // 2. Experience delay (let them use the product)
      aiNode(
        "2",
        "AI extract delivery + calc timing",
        280,
        "You are a post-purchase experience specialist. Parse the fulfillment payload and extract customer and product details. Calculate: review request delay (7 days for most products, 14 for skincare/supplements), depletion delay (based on product category and quantity), and review incentive. Respond JSON: {customer_email, customer_name, product_title, sku, quantity, review_delay_days, depletion_delay_days, review_incentive, product_category, order_id}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "product_title", as: "product_title", type: "string" },
          { field: "sku", as: "sku", type: "string" },
          { field: "quantity", as: "quantity", type: "number" },
          {
            field: "review_delay_days",
            as: "review_delay_days",
            type: "number",
          },
          {
            field: "depletion_delay_days",
            as: "depletion_delay_days",
            type: "number",
          },
          { field: "review_incentive", as: "review_incentive", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
        ],
      ),

      // 3. Review request email
      emailNode(
        "3",
        "Review request email",
        520,
        "{customer_email}",
        "How's your {product_title}? We'd love your feedback!",
        "Hi {customer_name},\\n\\nYou've had your {product_title} for a little while now — how are you liking it?\\n\\nWe'd really appreciate your honest review. It helps other customers and helps us improve.\\n\\nLeave a review: {review_link}\\n\\n{review_incentive}\\n\\nThanks for being a valued customer!",
      ),

      // 4. Depletion timing delay
      n("4", "delayAction", "Wait until depletion window", 760, {
        delayDetails: {
          delayType: "dynamic",
          delayField: "depletion_delay_days",
          delayUnit: "days",
        },
      }),

      // 5. Review check condition
      n("5", "condition", "Customer left a review?", 1000, {
        conditionData: [
          {
            variable: "customer_email",
            value: "",
            comparison_operator: "NOT EMPTY",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. "Thanks for reviewing" + reorder email (True)
      emailNode(
        "6",
        "Thank you + reorder email",
        1240,
        "{customer_email}",
        "Thanks for your review! Time to restock your {product_title}?",
        "Hi {customer_name},\\n\\nThank you for taking the time to review your {product_title} — your feedback means a lot to us!\\n\\nSpeaking of which, you might be running low. Based on your purchase of {quantity}x {product_title}, it's about time for a restock.\\n\\nReorder now: {reorder_link}\\n\\nAs a thank you for your review, enjoy free shipping on your next order with code THANKYOU-{order_id}.\\n\\nWe appreciate you!",
      ),

      // 7. "Review + reorder" combined email (False)
      emailNode(
        "7",
        "Combined review + reorder email",
        1240,
        "{customer_email}",
        "Running low on {product_title}? Reorder + share your experience",
        "Hi {customer_name},\\n\\nYour {product_title} supply might be running low — time for a refill!\\n\\nReorder now: {reorder_link}\\n\\nAlso, we'd still love to hear what you think! Your review helps other customers discover {product_title}.\\n\\nLeave a review: {review_link}\\n\\n{review_incentive}\\n\\nThanks for being a loyal customer!",
        400,
      ),

      // 8. Sequence performance logging
      sheetsNode(
        "8",
        "Log sequence performance to Sheets",
        1480,
        "ReviewReplenishSequence",
        '[["{customer_email}","{customer_name}","{product_title}","{sku}","{product_category}","{review_delay_days}","{depletion_delay_days}","{order_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Left review -> thank you + reorder
      e("5", "7", "output_2"), // No review -> combined email
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 506. REPLENISHMENT WITH SUBSCRIPTION UPSELL (8 nodes)
  // ===================================================================

  "Replenishment with Subscription Upsell": {
    summary:
      "Converts one-time consumable buyers to subscribers with economics comparison. A one-time consumable delivery triggers subscription economics calculation via runCode, checks if already subscribed, waits for replenishment timing, sends a side-by-side comparison email, tracks subscription conversion, alerts ops on pricing issues, and logs timing optimization data.",
    nodes: [
      trigger("Webhook: One-time consumable delivery", "webhook_app", {
        source: "Shopify",
        event: "fulfillments/create",
        howItStarts:
          "A one-time consumable product is delivered (not a subscription order), triggering a subscription conversion flow with clear savings comparison",
        setup:
          "Connect Shopify fulfillments/create webhook. The workflow identifies one-time consumable purchases and presents the subscription alternative with economic comparison.",
        inputFields: [],
      }),

      // 2. Subscription economics calc (runCode)
      n("2", "runCode", "Calculate subscription economics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate subscription economics comparison
const payload = JSON.parse(vars.webhook_payload || '{}');
const productTitle = payload.line_items?.[0]?.title || 'Unknown';
const sku = payload.line_items?.[0]?.sku || '';
const quantity = parseInt(payload.line_items?.[0]?.quantity || '1', 10);
const email = payload.email || payload.customer?.email || '';
const firstName = payload.customer?.first_name || 'Customer';
const oneTimePrice = parseFloat(payload.line_items?.[0]?.price || '0');

// Subscription pricing model
const subscriptionDiscount = 0.15; // 15% off for subscribers
const subscriptionPrice = (oneTimePrice * (1 - subscriptionDiscount)).toFixed(2);
const monthlySavings = (oneTimePrice - parseFloat(subscriptionPrice)).toFixed(2);
const annualSavings = (parseFloat(monthlySavings) * 12).toFixed(2);
const freeShippingThreshold = 25;
const includesFreeShipping = parseFloat(subscriptionPrice) >= freeShippingThreshold;
const shippingValue = includesFreeShipping ? 5.99 : 0;
const totalAnnualValue = (parseFloat(annualSavings) + shippingValue * 12).toFixed(2);

// Replenishment timing
const intervalDays = 30 * quantity;
const reminderDays = Math.max(1, intervalDays - 7);

return {
  customer_email: email,
  customer_first_name: firstName,
  product_title: productTitle,
  sku: sku,
  one_time_price: oneTimePrice.toFixed(2),
  subscription_price: subscriptionPrice,
  monthly_savings: monthlySavings,
  annual_savings: annualSavings,
  total_annual_value: totalAnnualValue,
  discount_percent: (subscriptionDiscount * 100).toFixed(0),
  includes_free_shipping: includesFreeShipping ? 'yes' : 'no',
  interval_days: intervalDays,
  reminder_delay_days: reminderDays,
  is_subscription_order: 'no'
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_first_name", type: "string" },
            { name: "product_title", type: "string" },
            { name: "sku", type: "string" },
            { name: "one_time_price", type: "string" },
            { name: "subscription_price", type: "string" },
            { name: "monthly_savings", type: "string" },
            { name: "annual_savings", type: "string" },
            { name: "total_annual_value", type: "string" },
            { name: "discount_percent", type: "string" },
            { name: "includes_free_shipping", type: "string" },
            { name: "interval_days", type: "number" },
            { name: "reminder_delay_days", type: "number" },
            { name: "is_subscription_order", type: "string" },
          ],
        },
      }),

      // 3. Already-subscribed check condition
      n("3", "condition", "Not already a subscriber?", 520, {
        conditionData: [
          {
            variable: "is_subscription_order",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Replenishment timing delay
      n("4", "delayAction", "Wait until replenishment window", 760, {
        delayDetails: {
          delayType: "dynamic",
          delayField: "reminder_delay_days",
          delayUnit: "days",
        },
      }),

      // 5. Comparison email (one-time vs subscribe)
      emailNode(
        "5",
        "Subscription comparison email",
        1000,
        "{customer_email}",
        "Save ${monthly_savings}/month on {product_title} with Subscribe & Save",
        "Hi {customer_first_name},\\n\\nYou recently purchased {product_title} at ${one_time_price}. Here's how subscribing compares:\\n\\nOne-Time Purchase:\\n- Price: ${one_time_price} per order\\n- Must remember to reorder\\n- Standard shipping\\n\\nSubscribe & Save ({discount_percent}% off):\\n- Price: ${subscription_price} per delivery\\n- Automatic deliveries every {interval_days} days\\n- Free shipping included\\n- Skip or cancel anytime\\n\\nYour savings:\\n- Monthly: ${monthly_savings}\\n- Annual: ${annual_savings}\\n- Total annual value (with free shipping): ${total_annual_value}\\n\\nSubscribe now: {subscribe_link}\\n\\nIt's the smarter way to shop!",
      ),

      // 6. Subscription conversion tracking
      sheetsNode(
        "6",
        "Log conversion attempt to Sheets",
        1240,
        "SubscriptionConversion",
        '[["{customer_email}","{product_title}","{sku}","{one_time_price}","{subscription_price}","{monthly_savings}","{annual_savings}","{discount_percent}","{interval_days}","attempted"]]',
      ),

      // 7. Ops pricing alert (Slack)
      slackNode(
        "7",
        "Slack: Ops pricing alert",
        1480,
        ":chart_with_upwards_trend: Subscription Upsell Sent\\nProduct: {product_title} ({sku})\\nCustomer: {customer_email}\\nOne-time: ${one_time_price} | Subscribe: ${subscription_price}\\nSavings: ${monthly_savings}/mo (${annual_savings}/yr)\\nDiscount: {discount_percent}%\\nFree shipping: {includes_free_shipping}\\nReview pricing accuracy.",
        400,
      ),

      // 8. Timing optimization logging
      sheetsNode(
        "8",
        "Log timing optimization to Sheets",
        1720,
        "ReplenishTimingOptimization",
        '[["{customer_email}","{product_title}","{interval_days}","{reminder_delay_days}","{discount_percent}","{includes_free_shipping}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // Not subscribed -> replenishment upsell
      e("3", "7", "output_2"), // Already subscribed -> pricing alert for review
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 507. RETURN MERCHANDISE AUTHORIZATION BOT (9 nodes)
  // ===================================================================

  "Return Merchandise Authorization Bot": {
    summary:
      "Automates RMA creation and return processing via chat. A chat return trigger authenticates the customer and pulls order data, checks return policy eligibility via runCode, branches on return vs exchange, creates the RMA record, generates a return shipping label, responds in-channel with the RMA number, escalates to the returns team on Slack, and logs the RMA outcome to Sheets.",
    nodes: [
      trigger("Webhook: Chat return request", "webhook_app", {
        source: "Chat Platform",
        event: "message.return_intent",
        howItStarts:
          "A customer initiates a return or exchange request through live chat or chatbot, triggering automated RMA creation with policy validation",
        setup:
          "Connect your chat platform to detect return/exchange intent and fire a webhook. Include conversation ID, customer message, and any order references.",
        inputFields: [],
      }),

      // 2. Customer auth + order pull
      aiNode(
        "2",
        "AI authenticate + pull order details",
        280,
        "You are a returns processing specialist. Parse the return request payload and extract: conversation ID, customer email, customer name, order number, product name, product SKU, purchase date, purchase amount, return reason, whether the customer wants an exchange or refund, exchange product preference (if applicable), and item condition description. Validate the customer identity. Respond JSON: {conversation_id, customer_email, customer_name, order_number, product_name, product_sku, purchase_date, purchase_amount, return_reason, return_type, exchange_product, item_condition, customer_authenticated}",
        "{webhook_payload}",
        [
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          { field: "purchase_date", as: "purchase_date", type: "string" },
          { field: "purchase_amount", as: "purchase_amount", type: "string" },
          { field: "return_reason", as: "return_reason", type: "string" },
          { field: "return_type", as: "return_type", type: "string" },
          { field: "exchange_product", as: "exchange_product", type: "string" },
          { field: "item_condition", as: "item_condition", type: "string" },
          {
            field: "customer_authenticated",
            as: "customer_authenticated",
            type: "string",
          },
        ],
      ),

      // 3. Policy eligibility check (runCode)
      n("3", "runCode", "Check return policy eligibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate return eligibility against policy rules
const purchaseDate = new Date(vars.purchase_date || Date.now());
const daysSincePurchase = Math.floor((Date.now() - purchaseDate.getTime()) / 86400000);
const returnWindowDays = 30;
const authenticated = vars.customer_authenticated === 'true';
const condition = (vars.item_condition || '').toLowerCase();
const reason = (vars.return_reason || '').toLowerCase();
const returnType = (vars.return_type || 'refund').toLowerCase();

const withinWindow = daysSincePurchase <= returnWindowDays;
const isDefective = reason.includes('defect') || reason.includes('broken') || reason.includes('faulty');
const acceptableCondition = condition.includes('new') || condition.includes('unused') || condition.includes('original') || isDefective;

// Defective items are always eligible regardless of window
const eligible = authenticated && (withinWindow || isDefective) && acceptableCondition;
const isExchange = returnType.includes('exchange') || returnType.includes('swap');
const restockingFee = !isDefective && daysSincePurchase > 14 ? 15 : 0;
const refundAmount = parseFloat(vars.purchase_amount || '0') * (1 - restockingFee / 100);
const rmaNumber = 'RMA-' + Date.now().toString(36).toUpperCase();

return {
  eligible: eligible ? 'yes' : 'no',
  is_exchange: isExchange ? 'yes' : 'no',
  days_since_purchase: daysSincePurchase,
  within_return_window: withinWindow ? 'yes' : 'no',
  is_defective: isDefective ? 'yes' : 'no',
  restocking_fee_percent: restockingFee,
  refund_amount: refundAmount.toFixed(2),
  rma_number: rmaNumber,
  denial_reason: !authenticated ? 'Authentication failed' : (!withinWindow && !isDefective ? 'Outside 30-day return window' : (!acceptableCondition ? 'Item condition not acceptable' : ''))
};`,
          outputVariables: [
            { name: "eligible", type: "string" },
            { name: "is_exchange", type: "string" },
            { name: "days_since_purchase", type: "number" },
            { name: "within_return_window", type: "string" },
            { name: "is_defective", type: "string" },
            { name: "restocking_fee_percent", type: "number" },
            { name: "refund_amount", type: "string" },
            { name: "rma_number", type: "string" },
            { name: "denial_reason", type: "string" },
          ],
        },
      }),

      // 4. Return vs exchange condition
      n("4", "condition", "Return eligible?", 760, {
        conditionData: [
          {
            variable: "eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. RMA creation
      n("5", "hitApi", "Create RMA record", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{returns_api}/api/v1/rma",
          headers: [
            { key: "Authorization", value: "Bearer {returns_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "rma_number", value: "{rma_number}" },
            { key: "order_number", value: "{order_number}" },
            { key: "customer_email", value: "{customer_email}" },
            { key: "product_sku", value: "{product_sku}" },
            { key: "return_type", value: "{return_type}" },
            { key: "return_reason", value: "{return_reason}" },
            { key: "refund_amount", value: "{refund_amount}" },
            { key: "exchange_product", value: "{exchange_product}" },
            { key: "is_defective", value: "{is_defective}" },
          ],
          variables: [
            { name: "id", customName: "rma_record_id", type: "string" },
            { name: "status", customName: "rma_status", type: "string" },
            {
              name: "created_at",
              customName: "rma_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Return label generation
      n("6", "hitApi", "Generate return shipping label", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.easypost.com/v2/shipments",
          headers: [
            { key: "Authorization", value: "Bearer {easypost_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "shipment",
              value:
                '{"to_address":{"id":"{warehouse_address_id}"},"from_address":{"id":"{customer_address_id}"},"parcel":{"predefined_package":"FedExMediumBox"},"is_return":true}',
            },
          ],
          variables: [
            {
              name: "postage_label.label_url",
              customName: "return_label_url",
              type: "string",
            },
            {
              name: "tracking_code",
              customName: "return_tracking_number",
              type: "string",
            },
            {
              name: "selected_rate.rate",
              customName: "return_shipping_cost",
              type: "string",
            },
          ],
        },
      }),

      // 7. In-channel response with RMA#
      n("7", "sendMessage", "Send RMA confirmation in chat", 1480, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "Your return has been approved!\n\nRMA Number: {rma_number}\nReturn type: {return_type}\nProduct: {product_name} (Order #{order_number})\nRefund amount: ${refund_amount}\nRestocking fee: {restocking_fee_percent}%\n\nReturn label: {return_label_url}\nTracking: {return_tracking_number}\n\nPlease ship within 14 days using the provided label. Your refund will be processed within 3-5 business days after we receive the item.\n\nExchange product: {exchange_product}",
        },
      }),

      // 8. Returns team escalation (Slack)
      slackNode(
        "8",
        "Slack: Returns team escalation",
        1720,
        ":leftwards_arrow_with_hook: RMA Created\\nRMA: {rma_number}\\nCustomer: {customer_name} ({customer_email})\\nOrder: #{order_number}\\nProduct: {product_name} ({product_sku})\\nType: {return_type}\\nReason: {return_reason}\\nDefective: {is_defective}\\nRefund: ${refund_amount}\\nExchange: {exchange_product}\\nLabel: {return_label_url}",
        400,
      ),

      // 9. RMA outcome logging (Sheets)
      sheetsNode(
        "9",
        "Log RMA to Sheets",
        1960,
        "RMALog",
        '[["{rma_number}","{order_number}","{customer_email}","{customer_name}","{product_name}","{product_sku}","{return_type}","{return_reason}","{is_defective}","{refund_amount}","{restocking_fee_percent}","{rma_status}","{return_tracking_number}","{denial_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Eligible -> create RMA
      e("4", "8", "output_2"), // Not eligible -> escalate to returns team
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
